import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeProvider } from 'styled-components';

import { theme } from './theme';
import { GlobalStyle } from './styles';
import {
  Button,
  ButtonRow,
  Card,
  CardTitle,
  Container,
  Field,
  FileIcon,
  fileIconKind,
  Hr,
  Input,
  Label,
  Main,
  MobileHide,
  MobileMenuBackdrop,
  Modal,
  MonoBox,
  Muted,
  Nav,
  NavLink,
  NavToggle,
  Overlay,
  Page,
  ProgressBar,
  ProgressWrap,
  Shell,
  Sidebar,
  Spinner,
  StepHeader,
  StepPills,
  StepTitle,
  Textarea,
  Topbar
} from './ui';

import { clearAuth, getAuth, setAuth } from './auth';
import { revokeRefreshToken, activatePlan, authOtpStart, authOtpVerify, cancelSubscription, checkin, commitMessage, createCryptoPayIntent, deleteAccount, deleteMessage, getCryptoMinAmount, getCryptoPayIntent, getProfile, getSettings, initMessage, listCryptoPayIntents, listMessages, markCryptoPayIntentCelebrated, ackLifetimeWelcome, redeemToken, saveSettings, uploadToPresignedUrl, type CryptoPayIntent, type Interval, type TrustedContact, type UserSettings } from './api';
import { track } from './analytics';
import FileDropzone, { simplifyMime } from './FileDropzone';
import { formatBytes } from './formatBytes';
import StorageMeter from './StorageMeter';
import { NextCheckInPulse } from './NextCheckInPulse';
import { ImOkayButton } from './ImOkayButton';
import { DispatchedScreen } from './DispatchedScreen';
import { SubscriptionPlans, type PlanInterval } from './SubscriptionPlans';
// billing is wired via checkout

function usePathRoute() {
  const [path, setPath] = useState(() => window.location.pathname || '/login');
  useEffect(() => {
    const on = () => setPath(window.location.pathname || '/login');
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);
  return path;
}

function navigate(path: string) {
  const p = path.startsWith('/') ? path : `/${path}`;
  window.history.pushState({}, '', p);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function Link({ to, children }: { to: string; children: any }) {
  return (
    <a
      href={to}
      onClick={(e) => {
        e.preventDefault();
        navigate(to);
      }}
    >
      {children}
    </a>
  );
}

type Route = '/login' | '/dashboard' | '/redeem' | '/settings' | '/billing' | '/done' | '/preview/dispatched' | '/onboarding';

function parseRoute(path: string): Route {
  const r = path || '/login';
  if (r.startsWith('/redeem')) return '/redeem';
  if (r.startsWith('/dashboard')) return '/dashboard';
  if (r.startsWith('/settings')) return '/settings';
  if (r.startsWith('/billing')) return '/billing';
  if (r.startsWith('/onboarding')) return '/onboarding';
  if (r.startsWith('/done')) return '/done';
  if (r.startsWith('/preview/dispatched')) return '/preview/dispatched';
  return '/login';
}

const PUBLIC_ROUTES = new Set<Route>(['/login', '/redeem', '/preview/dispatched']);

function LoadFailed({ offline, onRetry, onSignOut }: { offline: boolean; onRetry: () => void; onSignOut: () => void }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '60vh', textAlign: 'center', gap: 8 }}>
      <CardTitle style={{ margin: 0 }}>{offline ? "You're offline" : "Couldn't load your account"}</CardTitle>
      <Muted style={{ margin: 0 }}>
        {offline ? "We'll reconnect automatically when you're back online." : 'Check your connection and try again.'}
      </Muted>
      <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginTop: 12 }}>
        {offline ? null : <Button onClick={onRetry}>Try again</Button>}
        <a
          href="/login"
          onClick={(e) => { e.preventDefault(); onSignOut(); }}
          style={{ fontSize: 14, opacity: 0.7 }}
        >
          Sign out
        </a>
      </div>
    </div>
  );
}

export default function App() {
  const path = usePathRoute();
  const route = parseRoute(path);

  // Page view per route change. Anonymous when logged out, authed when logged
  // in — analytics.ts picks the right endpoint based on getIdToken().
  useEffect(() => { track('page_view', { route }); }, [route]);

  const [auth, setAuthState] = useState(() => getAuth());
  const authed = !!auth.idToken;

  // Onboarded resolution lives at the App level so we never render the
  // dashboard before knowing whether the user still needs onboarding.
  // 'unknown' means the request is in flight; we render nothing for
  // protected routes during this time to avoid a flash of dashboard.
  const [onboardedState, setOnboardedState] = useState<'unknown' | 'yes' | 'no' | 'error'>('unknown');
  // Bumped by "Try again" (and by coming back online) to re-run the check.
  const [onboardedAttempt, setOnboardedAttempt] = useState(0);
  const onboardedStateRef = useRef(onboardedState);
  onboardedStateRef.current = onboardedState;
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);

  useEffect(() => {
    const goOffline = () => setOffline(true);
    const goOnline = () => {
      setOffline(false);
      // Back online — re-run the check automatically if it had given up.
      if (onboardedStateRef.current === 'error') setOnboardedAttempt((n) => n + 1);
    };
    window.addEventListener('offline', goOffline);
    window.addEventListener('online', goOnline);
    return () => {
      window.removeEventListener('offline', goOffline);
      window.removeEventListener('online', goOnline);
    };
  }, []);

  useEffect(() => {
    if (!authed || !auth.idToken) {
      setOnboardedState('unknown');
      return;
    }
    let cancelled = false;
    setOnboardedState('unknown');
    // Most failures here are transient network blips, so retry quietly
    // (spinner stays up) before surfacing an error: 3 attempts total, with
    // 1s then 3s pauses between them.
    const delays = [1000, 3000];
    const attempt = async () => {
      for (let i = 0; ; i++) {
        try {
          const r = await getSettings(auth.idToken!);
          if (!cancelled) setOnboardedState(r.settings.onboarded ? 'yes' : 'no');
          return;
        } catch {
          if (cancelled) return;
          // apiFetch clears auth when the session can't be refreshed — in
          // that case drop to the login form rather than retrying/erroring.
          if (!getAuth().idToken) { setAuthState(getAuth()); return; }
          if (i >= delays.length) { setOnboardedState('error'); return; }
          await new Promise((res) => setTimeout(res, delays[i]));
          if (cancelled) return;
        }
      }
    };
    void attempt();
    return () => { cancelled = true; };
  }, [authed, auth.idToken, onboardedAttempt]);

  const refreshOnboarded = () => {
    if (!auth.idToken) return;
    getSettings(auth.idToken)
      .then(r => setOnboardedState(r.settings.onboarded ? 'yes' : 'no'))
      .catch(() => {});
  };

  useEffect(() => {
    if (!authed) {
      if (!PUBLIC_ROUTES.has(route)) navigate('/login');
      return;
    }
    if (route === '/login') {
      if (onboardedState === 'no') navigate('/onboarding');
      else if (onboardedState === 'yes') navigate('/dashboard');
      return;
    }
    if (PUBLIC_ROUTES.has(route)) return;
    if (onboardedState === 'no' && route !== '/onboarding') navigate('/onboarding');
    if (onboardedState === 'yes' && route === '/onboarding') navigate('/dashboard');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route, authed, onboardedState]);

  const onSignedIn = () => setAuthState(getAuth());
  const onSignedOut = () => {
    revokeRefreshToken();
    clearAuth();
    setAuthState(getAuth());
    setOnboardedState('unknown');
    navigate('/login');
  };

  // Hold rendering while we still don't know the user's onboarded state. This
  // covers two flashes:
  //   1. Protected routes — keeps the dashboard from flashing behind the
  //      onboarding modal on a fresh account.
  //   2. /login while authed — when a logged-in user lands here (refresh,
  //      typed URL), we don't want the login form to flash before the
  //      redirect-to-dashboard effect fires.
  // /redeem and /preview/dispatched are intentionally not held: they're
  // public-by-design and don't depend on account state.
  const holdForOnboardingCheck =
    authed && onboardedState === 'unknown' && (!PUBLIC_ROUTES.has(route) || route === '/login');
  // The check gave up after its retries — show a retry screen in the same
  // spot instead of spinning forever.
  const onboardedCheckFailed =
    authed && onboardedState === 'error' && (!PUBLIC_ROUTES.has(route) || route === '/login');

  // Once we know the user isn't onboarded, render OnboardingPage directly
  // even if `route` hasn't caught up yet — otherwise there's a one-render
  // gap where AuthedShell mounts (and returns null while its own profile
  // fetch runs), giving a brief white screen before the modal appears.
  const showOnboardingNow = authed && onboardedState === 'no' && (!PUBLIC_ROUTES.has(route) || route === '/login');

  // An authed user on /login (which is also what `/` parses to) gets
  // redirected by the effect above — but effects run after paint, so the
  // render in between would flash the login form for a frame. Render the
  // destination directly instead; the effect then just syncs the URL.
  const renderRoute: Route = authed && route === '/login' && onboardedState === 'yes' ? '/dashboard' : route;

  // Create flow is a transient modal, not a route — its state lives in
  // memory and a page reload simply closes it (matches how it always behaved
  // since reload wiped form state anyway).
  const [showCreate, setShowCreate] = useState(false);
  // Bumped after a successful create so the dashboard refreshes its message
  // list — only messages, not settings (settings don't change on create).
  const [messagesVersion, setMessagesVersion] = useState(0);
  const onOpenCreate = () => setShowCreate(true);
  const onCloseCreate = (created: boolean) => {
    setShowCreate(false);
    if (created) setMessagesVersion((v) => v + 1);
  };

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Page>
        <Container>
          {route !== '/redeem' ? (
            <Topbar>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Link to="/">
                  <img src="/logo.png" alt="SilentPost" style={{ height: 32, cursor: 'pointer', display: 'block' }} />
                </Link>
              </div>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }} />
            </Topbar>
          ) : null}

          {holdForOnboardingCheck ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
              <Spinner size={32} />
            </div>
          )
            : onboardedCheckFailed ? (
              <LoadFailed
                offline={offline}
                onRetry={() => setOnboardedAttempt((n) => n + 1)}
                onSignOut={onSignedOut}
              />
            ) : showOnboardingNow ? (
              <OnboardingPage idToken={auth.idToken!} onDone={() => { refreshOnboarded(); navigate('/dashboard'); }} />
            ) : renderRoute === '/preview/dispatched' ? (
              <DispatchedPreview />
            ) : renderRoute === '/redeem' ? (
              <RedeemPage />
            ) : renderRoute === '/login' ? (
              <LoginPage onSignedIn={onSignedIn} />
            ) : renderRoute === '/done' ? (
              <DoneRoute onSignOut={onSignedOut} />
            ) : renderRoute === '/onboarding' ? (
              <OnboardingPage idToken={auth.idToken!} onDone={() => { refreshOnboarded(); navigate('/dashboard'); }} />
            ) : (
              <AuthedShell route={renderRoute} email={auth.email} onLogout={onSignedOut} onOpenCreate={onOpenCreate} messagesVersion={messagesVersion} />
            )}

          {!holdForOnboardingCheck && !onboardedCheckFailed && showCreate && auth.idToken ? <CreateFlowOverlay idToken={auth.idToken} onClose={onCloseCreate} /> : null}
        </Container>
      </Page>
    </ThemeProvider>
  );
}

function OnboardingPage({ idToken, onDone }: { idToken: string; onDone: () => void }) {
  return <OnboardingModal idToken={idToken} onDone={onDone} />;
}

function DoneRoute({ onSignOut }: { onSignOut: () => void }) {
  const auth = useMemo(() => getAuth(), []);
  const [tripped, setTripped] = useState<boolean | null>(null);

  useEffect(() => {
    if (!auth.idToken) return;
    getProfile(auth.idToken)
      .then(p => setTripped(!!p.profile.trippedAt))
      .catch(() => setTripped(false));
  }, [auth.idToken]);

  useEffect(() => {
    if (tripped === false) navigate('/dashboard');
  }, [tripped]);

  if (tripped !== true) return null;
  return <DispatchedScreen onSignOut={onSignOut} />;
}

function DispatchedPreview() {
  const [replayKey, setReplayKey] = useState(0);
  return (
    <>
      <DispatchedScreen
        key={replayKey}
        onSignOut={() => alert('(preview) sign-out would fire here')}
      />
      <div
        style={{
          position: 'fixed',
          top: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 10001,
          background: 'rgba(0,0,0,0.55)',
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(200, 173, 106, 0.25)',
          borderRadius: 999,
          padding: 6,
        }}
      >
        <button
          onClick={() => setReplayKey(k => k + 1)}
          style={{
            appearance: 'none',
            border: 'none',
            background: 'transparent',
            color: 'rgba(233, 221, 191, 0.7)',
            padding: '8px 16px',
            borderRadius: 999,
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: 13,
          }}
        >
          Replay
        </button>
      </div>
    </>
  );
}

function ordinalDay(d: number): string {
  const k = d % 100;
  if (k >= 11 && k <= 13) return `${d}th`;
  switch (d % 10) {
    case 1: return `${d}st`;
    case 2: return `${d}nd`;
    case 3: return `${d}rd`;
    default: return `${d}th`;
  }
}

function fmtDateOrdinal(epochSec: number): string {
  const d = new Date(epochSec * 1000);
  const month = d.toLocaleDateString(undefined, { month: 'short' });
  return `${month} ${ordinalDay(d.getDate())}, ${d.getFullYear()}`;
}

// Spring-pop the circle, pulse an outer ring, then draw the check from
// bottom-left to top-right. Pass a different `replayKey` to re-trigger the
// animation (changing the key remounts the element).
const SUCCESS_CHECK_KEYFRAMES = `
  @keyframes sp-check-spring {
    0%   { transform: scale(0);    opacity: 0; }
    50%  { transform: scale(1.15); opacity: 1; }
    75%  { transform: scale(0.96); }
    100% { transform: scale(1); }
  }
  @keyframes sp-check-ring {
    0%   { transform: scale(1);   opacity: 0.55; }
    100% { transform: scale(2.0); opacity: 0; }
  }
  @keyframes sp-check-draw {
    to { stroke-dashoffset: 0; }
  }
`;

function SuccessCheck({
  size = 72,
  replayKey = 0,
}: { size?: number; replayKey?: number | string }) {
  const PATH_LEN = 25;

  return (
    <>
      <style>{SUCCESS_CHECK_KEYFRAMES}</style>
      <div
        key={replayKey}
        style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            border: '1px solid rgba(98,176,121,0.55)',
            animation: 'sp-check-ring 950ms cubic-bezier(0.22, 0.61, 0.36, 1) 160ms both',
            pointerEvents: 'none',
          }}
        />
        <div
          style={{
            width: size,
            height: size,
            borderRadius: '50%',
            background: 'rgba(98,176,121,0.12)',
            border: '1px solid rgba(98,176,121,0.35)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#8fd5a5',
            animation: 'sp-check-spring 720ms cubic-bezier(0.34, 1.56, 0.64, 1) both',
          }}
        >
          <svg width={size * 0.5} height={size * 0.5} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path
              d="M4 12L9 17L20 6"
              style={{
                strokeDasharray: PATH_LEN,
                strokeDashoffset: PATH_LEN,
                animation: 'sp-check-draw 420ms cubic-bezier(0.65, 0, 0.35, 1) 400ms forwards',
              }}
            />
          </svg>
        </div>
      </div>
    </>
  );
}

type SubscriptionStatus = 'active' | 'free' | 'expiring' | 'expired';

const CHEAPEST_PLAN_CENTS = 700; // monthly

function computeSubscriptionStatus(
  planInterval: string | null | undefined,
  planActiveUntil: number | null | undefined,
  creditsUsdCents: number,
  cancelledAt: number | null | undefined,
  nowSec: number
): { status: SubscriptionStatus; daysUntilExpiry: number | null } {
  if (planInterval === 'lifetime') return { status: 'active', daysUntilExpiry: null };
  if (planInterval && planInterval !== 'free' && Number(planActiveUntil ?? 0) > nowSec) {
    const daysUntilExpiry = Math.ceil((Number(planActiveUntil) - nowSec) / 86400);
    // A cancelled-but-still-active plan stays 'active' to suppress the
    // expiring banner — the user already chose to let it lapse.
    if (!cancelledAt && daysUntilExpiry < 7 && creditsUsdCents < CHEAPEST_PLAN_CENTS) {
      return { status: 'expiring', daysUntilExpiry };
    }
    return { status: 'active', daysUntilExpiry };
  }
  if (planInterval && planInterval !== 'free') return { status: 'expired', daysUntilExpiry: null };
  return { status: 'free', daysUntilExpiry: null };
}

function SubscriptionAlert({
  status,
  daysUntilExpiry,
  onResubscribe,
  style,
}: {
  status: SubscriptionStatus;
  daysUntilExpiry?: number | null;
  onResubscribe?: () => void;
  style?: React.CSSProperties;
}) {
  if (status === 'active') return null;
  const isExpired = status === 'expired';
  const isExpiring = status === 'expiring';

  const teal = {
    border: '1px solid rgba(110,180,185,0.45)',
    bg: 'linear-gradient(180deg, rgba(110,180,185,0.16), rgba(110,180,185,0.10))',
  };
  const red = {
    border: '1px solid rgba(210,80,80,0.45)',
    bg: 'linear-gradient(180deg, rgba(210,80,80,0.18), rgba(210,80,80,0.10))',
  };
  const skin = isExpired ? red : teal;

  const copy = isExpired
    ? 'Your plan expired. Resubscribe to keep delivery active.'
    : isExpiring
      ? `Your plan expires in ${daysUntilExpiry ?? 0} ${(daysUntilExpiry ?? 0) === 1 ? 'day' : 'days'}. Add credits to keep delivery active.`
      : 'Automatic delivery to recipients requires an active plan.';

  return (
    <div
      style={{
        padding: '12px 16px',
        borderRadius: 10,
        border: skin.border,
        background: skin.bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        fontSize: 13,
        lineHeight: 1.45,
        ...(style ?? {}),
      }}
    >
      <span style={{ color: theme.colors.text, fontWeight: 600 }}>{copy}</span>
      {isExpired && onResubscribe && (
        <a
          href="#"
          onClick={(e) => { e.preventDefault(); onResubscribe(); }}
          style={{
            color: '#f0a8a8',
            fontWeight: 700,
            fontSize: 13,
            whiteSpace: 'nowrap',
            padding: '6px 14px',
            borderRadius: 8,
            border: '1px solid rgba(210,80,80,0.55)',
            background: 'rgba(210,80,80,0.10)',
            cursor: 'pointer',
          }}
        >
          Resubscribe
        </a>
      )}
    </div>
  );
}

// Store URLs for the companion apps.
const APP_STORE_URL = 'https://apps.apple.com/us/app/silentpost/id6767236583';
const PLAY_STORE_URL: string | null = 'https://play.google.com/store/apps/details?id=com.silentpost.app';
const APP_BANNER_DISMISSED_KEY = 'sp_app_banner_dismissed';

type MobilePlatform = 'ios' | 'android' | 'other';

function detectMobilePlatform(): MobilePlatform {
  const ua = navigator.userAgent || '';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

// Suggests the native apps. Only rendered for users without a registered push
// device (see get-profile's hasDevice). Dismissal is sticky via localStorage —
// a download nudge isn't urgent enough to keep re-showing once waved off.
function AppDownloadBanner({ onDismiss }: { onDismiss: () => void }) {
  const platform = useMemo(detectMobilePlatform, []);

  useEffect(() => { track('app_banner_view', { platform }); }, [platform]);

  const storeLink = (store: 'ios' | 'android', label: string, href: string) => (
    <a
      key={store}
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => track('app_banner_click', { store })}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 14px',
        borderRadius: 10,
        border: '1px solid rgba(200,173,106,0.45)',
        background: 'rgba(200,173,106,0.10)',
        color: theme.colors.text,
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: 'nowrap',
        textDecoration: 'none',
      }}
    >
      {label}
    </a>
  );

  return (
    <BannerShell>
      <BannerBody>
        <div style={{ flex: '1 1 240px', minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: theme.colors.text }}>
            Never miss a check-in
          </div>
          <div style={{ fontSize: 13, color: theme.colors.muted, marginTop: 3, lineHeight: 1.45 }}>
            Get the SilentPost app for push reminders and one-tap check-ins.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(() => {
            const ios = APP_STORE_URL && platform !== 'android'
              ? storeLink('ios', 'App Store', APP_STORE_URL) : null;
            const android = PLAY_STORE_URL && platform !== 'ios'
              ? storeLink('android', 'Google Play', PLAY_STORE_URL) : null;
            const links = [ios, android].filter(Boolean);
            // Fall back to every available store link so we never render an empty
            // row (e.g. an Android visitor while only the iOS app exists).
            return links.length ? links : [
              APP_STORE_URL && storeLink('ios', 'App Store', APP_STORE_URL),
              PLAY_STORE_URL && storeLink('android', 'Google Play', PLAY_STORE_URL),
            ].filter(Boolean);
          })()}
        </div>
      </BannerBody>
      <BannerDismiss
        type="button"
        aria-label="Dismiss"
        title="Dismiss"
        onClick={() => { track('app_banner_dismiss', { platform }); onDismiss(); }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
          <path d="M6 6l12 12" />
          <path d="M18 6L6 18" />
        </svg>
      </BannerDismiss>
    </BannerShell>
  );
}

// The dismiss control gets its own column so it can never sit on top of the
// store buttons. On wide screens the whole row is centred; once the body wraps
// (phones) the X pins to the top, level with the title.
const BannerShell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 10px 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(200,173,106,0.4);
  background: linear-gradient(180deg, rgba(200,173,106,0.14), rgba(200,173,106,0.07));

  @media (max-width: 640px) {
    align-items: flex-start;
  }
`;

const BannerBody = styled.div`
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

const BannerDismiss = styled.button`
  flex: 0 0 auto;
  appearance: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: rgba(233,237,243,0.4);
  cursor: pointer;
  transition: color 0.15s, background 0.15s, border-color 0.15s;

  &:hover {
    color: ${({ theme }) => theme.colors.text};
    background: rgba(200,173,106,0.10);
    border-color: rgba(200,173,106,0.25);
  }
  &:focus-visible {
    outline: none;
    border-color: ${({ theme }) => theme.colors.gold};
  }
  @media (max-width: 640px) {
    margin-top: -4px;
  }
`;

function AuthedShell(props: { route: Route; email: string | null; onLogout: () => void; onOpenCreate: () => void; messagesVersion: number }) {
  const active = props.route;
  const auth = useMemo(() => getAuth(), []);
  const idToken = auth.idToken;
  const [profile, setProfile] = useState<{ storageUsedBytes: number; storageLimitBytes: number; trippedAt?: number | null; planInterval?: string | null; planActiveUntil?: number | null; creditsUsdCents?: number; cancelledAt?: number | null; hasDevice?: boolean; lifetimeGrant?: 'first-100' | 'admin' | null; lifetimeWelcomedAt?: number | null } | null>(null);
  const [navOpen, setNavOpen] = useState(false);
  const [lifetimeWelcome, setLifetimeWelcome] = useState(false);
  const lifetimeWelcomeFiredRef = useRef(false);
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeInitialPlan, setSubscribeInitialPlan] = useState<PlanInterval | null>(null);
  const [intents, setIntents] = useState<CryptoPayIntent[]>([]);
  const [paidIntent, setPaidIntent] = useState<CryptoPayIntent | null>(null);
  // In-memory guard for the gap between firing the celebration and the next
  // intents refresh reflecting the persisted celebratedAt — keeps a duplicate
  // modal from popping during that race.
  const triggeredCelebrationRef = useRef<Set<string>>(new Set());

  const refreshProfile = useCallback(async () => {
    if (!idToken) return;
    try {
      const p = await getProfile(idToken);
      setProfile({
        storageUsedBytes: p.profile.storageUsedBytes,
        storageLimitBytes: 5 * 1024 * 1024 * 1024,
        trippedAt: p.profile.trippedAt ?? null,
        planInterval: p.profile.planInterval ?? 'free',
        planActiveUntil: p.profile.planActiveUntil ?? null,
        creditsUsdCents: p.profile.creditsUsdCents ?? 0,
        cancelledAt: p.profile.cancelledAt ?? null,
        hasDevice: p.profile.hasDevice ?? false,
        lifetimeGrant: p.profile.lifetimeGrant ?? null,
        lifetimeWelcomedAt: p.profile.lifetimeWelcomedAt ?? null,
      });
    } catch { /* ignore */ }
  }, [idToken]);

  const refreshIntents = useCallback(async () => {
    if (!idToken) return;
    try {
      const r = await listCryptoPayIntents(idToken);
      setIntents(r.intents ?? []);
    } catch { /* ignore */ }
  }, [idToken]);

  useEffect(() => { refreshProfile(); }, [refreshProfile]);
  useEffect(() => { refreshIntents(); }, [refreshIntents]);

  useEffect(() => {
    if (profile?.trippedAt) navigate('/done');
  }, [profile?.trippedAt]);

  // One entry point for the celebration so both the AuthedShell poller and
  // modal-local pollers (AddCreditsModal, SubscribeModal) share the same
  // shown-flag bookkeeping. Persistence is server-side (intent.celebratedAt);
  // the ref only suppresses re-fires inside the gap before that write lands.
  const triggerCelebration = useCallback((paid: CryptoPayIntent) => {
    if (triggeredCelebrationRef.current.has(paid.intentId)) return;
    if (paid.celebratedAt) return;
    triggeredCelebrationRef.current.add(paid.intentId);
    setPaidIntent(paid);
    track(paid.targetPlan ? 'subscribe_paid' : 'credits_paid', {
      targetPlan: paid.targetPlan ?? null,
      amountUsdCents: paid.amountUsdCents,
    });
    if (idToken) {
      markCryptoPayIntentCelebrated({ idToken, intentId: paid.intentId })
        .catch(() => { /* best-effort; the ref still suppresses this session */ });
    }
    refreshProfile();
    refreshIntents();
  }, [idToken, refreshProfile, refreshIntents]);

  // Detect PAID transitions on intents we haven't celebrated yet. The
  // !celebratedAt check makes this stick across reloads / browsers / devices.
  useEffect(() => {
    const justPaid = intents.find(it =>
      it.status === 'PAID' && !it.celebratedAt && !triggeredCelebrationRef.current.has(it.intentId)
    );
    if (justPaid) triggerCelebration(justPaid);
  }, [intents, triggerCelebration]);

  // One-time "lifetime, on us" welcome for comped accounts. Persistence is
  // server-side (profile.lifetimeWelcomedAt); the ref only stops a re-fire in
  // the gap before the ack lands.
  useEffect(() => {
    if (!profile?.lifetimeGrant || profile.lifetimeWelcomedAt || lifetimeWelcomeFiredRef.current) return;
    lifetimeWelcomeFiredRef.current = true;
    setLifetimeWelcome(true);
    track('lifetime_welcome_shown', { grant: profile.lifetimeGrant });
  }, [profile?.lifetimeGrant, profile?.lifetimeWelcomedAt]);

  const closeLifetimeWelcome = useCallback(() => {
    setLifetimeWelcome(false);
    if (idToken) {
      ackLifetimeWelcome(idToken)
        .catch(() => { /* best-effort; the ref still suppresses this session */ })
        .finally(refreshProfile);
    }
  }, [idToken, refreshProfile]);

  // Live polling only while there's at least one pending intent inside the
  // 15-minute active window. Once they all expire / complete, polling stops.
  useEffect(() => {
    if (!idToken) return;
    const nowSec = Math.floor(Date.now() / 1000);
    const hasLive = intents.some(it =>
      (it.status === 'PENDING' || it.status === 'CONFIRMING') &&
      typeof it.createdAt === 'number' &&
      (nowSec - it.createdAt) < 15 * 60
    );
    if (!hasLive) return;
    const t = window.setInterval(refreshIntents, 4000);
    return () => window.clearInterval(t);
  }, [intents, idToken, refreshIntents]);

  const navTo = (to: string) => (e: any) => {
    e.preventDefault();
    setNavOpen(false);
    navigate(to);
  };

  if (!profile || profile.trippedAt) return null;

  const nowSec = Math.floor(Date.now() / 1000);
  const { status: subscriptionStatus, daysUntilExpiry } = computeSubscriptionStatus(
    profile.planInterval ?? 'free',
    profile.planActiveUntil ?? null,
    profile.creditsUsdCents ?? 0,
    profile.cancelledAt ?? null,
    nowSec
  );
  const planActive = subscriptionStatus === 'active' || subscriptionStatus === 'expiring';
  const isLifetime = profile.planInterval === 'lifetime';
  const currentSubInterval: PlanInterval | null =
    planActive && (profile.planInterval === 'monthly' || profile.planInterval === 'yearly')
      ? (profile.planInterval as PlanInterval)
      : null;
  const openSubscribeModal = (initial?: PlanInterval | null) => {
    setNavOpen(false);
    setSubscribeInitialPlan(initial ?? null);
    setSubscribeOpen(true);
  };

  // Resubscribe pre-targets the user's previous plan (monthly/yearly) so they
  // don't have to re-pick. Lifetime never lapses, so it's not in the set.
  const onResubscribe = () => {
    const prev = profile.planInterval;
    openSubscribeModal(prev === 'monthly' || prev === 'yearly' ? prev : null);
  };

  return (
    <Shell>
      <NavToggle
        active={navOpen}
        aria-label="Menu"
        aria-expanded={navOpen}
        onClick={() => setNavOpen(o => !o)}
      >
        <span />
        <span />
      </NavToggle>
      <MobileMenuBackdrop open={navOpen} onClick={() => setNavOpen(false)} aria-hidden />
      <Sidebar open={navOpen}>
        <Card>
          <Muted>{props.email ?? '—'}</Muted>
          <div style={{ marginTop: 10 }}>
            <StorageMeter
              bare
              loading={!profile}
              usedBytes={profile?.storageUsedBytes ?? 0}
              limitBytes={5 * 1024 * 1024 * 1024}
            />
          </div>
          <Hr />
          <Nav>
            <NavLink href="/dashboard" onClick={navTo('/dashboard')} active={active === '/dashboard'}>
              <span>Dashboard</span>
            </NavLink>

            <NavLink
              href="#create"
              onClick={(e: any) => { e.preventDefault(); setNavOpen(false); props.onOpenCreate(); }}
              active={false}
            >
              <span>Create</span>
            </NavLink>

            <NavLink href="/billing" onClick={navTo('/billing')} active={active === '/billing'}>
              <span>Billing</span>
            </NavLink>

            <NavLink href="/settings" onClick={navTo('/settings')} active={active === '/settings'}>
              <span>Settings</span>
            </NavLink>

            {!planActive && (
              <Button onClick={() => { setNavOpen(false); setSubscribeOpen(true); }} style={{ width: '100%', justifyContent: 'center', marginTop: 4 }}>
                Subscribe
              </Button>
            )}
          </Nav>
          <Hr />
          <ButtonRow>
            <Button variant="ghost" onClick={props.onLogout}>
              Sign out
            </Button>
          </ButtonRow>
        </Card>
      </Sidebar>

      <Main>
        {active === '/dashboard' ? (
          <DashboardPage
            trippedAt={profile?.trippedAt ?? null}
            hasDevice={profile?.hasDevice ?? false}
            subscriptionStatus={subscriptionStatus}
            daysUntilExpiry={daysUntilExpiry}
            onResubscribe={onResubscribe}
            onOpenCreate={props.onOpenCreate}
            messagesVersion={props.messagesVersion}
          />
        ) : active === '/billing' ? (
          <BillingPage
            intents={intents}
            refreshIntents={refreshIntents}
            refreshProfile={refreshProfile}
            onPaymentReceived={triggerCelebration}
            subscriptionStatus={subscriptionStatus}
            daysUntilExpiry={daysUntilExpiry}
            isLifetime={isLifetime}
            onOpenSubscribe={openSubscribeModal}
            onResubscribe={onResubscribe}
          />
        ) : active === '/settings' ? (
          <SettingsPage
            subscriptionStatus={subscriptionStatus}
            daysUntilExpiry={daysUntilExpiry}
            onResubscribe={onResubscribe}
          />
        ) : (
          <PlaceholderPage title="Coming soon" />
        )}
      </Main>

      {subscribeOpen && idToken && (
        <SubscribeModal
          idToken={idToken}
          creditsUsdCents={profile?.creditsUsdCents ?? 0}
          initialPlan={subscribeInitialPlan}
          currentSubInterval={currentSubInterval}
          alreadyCancelled={!!profile?.cancelledAt}
          onClose={() => { setSubscribeOpen(false); setSubscribeInitialPlan(null); refreshIntents(); }}
          onPaid={(paid) => { setSubscribeOpen(false); setSubscribeInitialPlan(null); triggerCelebration(paid); }}
          onCancelled={() => { setSubscribeOpen(false); setSubscribeInitialPlan(null); refreshProfile(); }}
        />
      )}

      {paidIntent && (
        <PaymentReceivedModal intent={paidIntent} onClose={() => setPaidIntent(null)} />
      )}

      {lifetimeWelcome && (
        <LifetimeWelcomeModal onClose={closeLifetimeWelcome} />
      )}
    </Shell>
  );
}

function PaymentReceivedModal({ intent, onClose }: { intent: CryptoPayIntent; onClose: () => void }) {
  const planLabel = intent.targetPlan
    ? intent.targetPlan.charAt(0).toUpperCase() + intent.targetPlan.slice(1)
    : null;
  const title = planLabel ? `Welcome to ${planLabel}` : 'Payment received';
  const isLifetime = planLabel === 'Lifetime';
  const primaryLine = planLabel
    ? (isLifetime ? 'Your plan is active.' : `Your ${planLabel} plan is active.`)
    : `$${(intent.amountUsdCents / 100).toFixed(2)} has been credited to your account.`;
  const secondaryLine = planLabel
    ? (isLifetime ? 'Automatic delivery will not lapse.' : 'Automatic delivery is enabled.')
    : null;

  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
          <SuccessCheck size={72} />
        </div>
        <CardTitle style={{ textAlign: 'center', margin: 0 }}>{title}</CardTitle>
        <Muted style={{ textAlign: 'center', marginTop: 6 }}>{primaryLine}</Muted>
        {secondaryLine && (
          <Muted style={{ textAlign: 'center', marginTop: 4 }}>{secondaryLine}</Muted>
        )}
        <Hr />
        <ButtonRow>
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Done</Button>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

// Shown once to accounts that were comped a lifetime plan (first-100 grant).
// Copy mirrors the welcome email in infra/lambda/lifetime-welcome-email.ts.
function LifetimeWelcomeModal({ onClose }: { onClose: () => void }) {
  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: 420 }}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
          <SuccessCheck size={72} />
        </div>
        <CardTitle style={{ textAlign: 'center', margin: 0 }}>Lifetime, on us.</CardTitle>
        <Muted style={{ textAlign: 'center', marginTop: 8 }}>
          You're one of the first to trust SilentPost with the things that matter. So your Lifetime plan is active — no subscription, no renewal, ever.
        </Muted>
        <Muted style={{ textAlign: 'center', marginTop: 10, color: theme.colors.gold }}>
          Thank you for trusting us.
        </Muted>
        <Hr />
        <ButtonRow>
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

function Toggle({
  checked,
  onChange,
  label,
  disabled,
  style,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label?: string;
  disabled?: boolean;
  style?: React.CSSProperties;
}) {
  const gold = theme.colors.gold;
  const onKey = (e: React.KeyboardEvent) => {
    if (disabled) return;
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      onChange(!checked);
    }
  };
  const toggle = (
    <span
      role="switch"
      aria-checked={checked}
      aria-disabled={disabled}
      tabIndex={disabled ? -1 : 0}
      onClick={(e) => { e.stopPropagation(); if (!disabled) onChange(!checked); }}
      onKeyDown={onKey}
      style={{
        position: 'relative',
        display: 'inline-block',
        width: 40,
        height: 22,
        borderRadius: 999,
        border: `1px solid ${checked ? 'rgba(200,173,106,0.55)' : 'rgba(210,180,91,0.22)'}`,
        background: checked ? 'rgba(200,173,106,0.22)' : 'rgba(11,13,16,0.55)',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: 'background 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
        outline: 'none',
        flexShrink: 0,
        boxShadow: checked ? `inset 0 0 0 1px rgba(200,173,106,0.18)` : 'none',
      }}
      onFocus={(e) => {
        (e.currentTarget as HTMLSpanElement).style.boxShadow =
          `${checked ? 'inset 0 0 0 1px rgba(200,173,106,0.18), ' : ''}0 0 0 3px rgba(210,180,91,0.18)`;
      }}
      onBlur={(e) => {
        (e.currentTarget as HTMLSpanElement).style.boxShadow = checked
          ? 'inset 0 0 0 1px rgba(200,173,106,0.18)'
          : 'none';
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: 2,
          width: 16,
          height: 16,
          borderRadius: '50%',
          background: checked ? gold : 'rgba(233,221,191,0.62)',
          transform: `translateX(${checked ? 18 : 0}px)`,
          transition: 'transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1), background 180ms ease, box-shadow 180ms ease',
          boxShadow: checked
            ? '0 0 10px rgba(200,173,106,0.55), 0 1px 2px rgba(0,0,0,0.4)'
            : '0 1px 2px rgba(0,0,0,0.35)',
        }}
      />
    </span>
  );

  if (!label) {
    return <div style={style}>{toggle}</div>;
  }

  return (
    <div
      onClick={() => !disabled && onChange(!checked)}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
        userSelect: 'none',
        ...style,
      }}
    >
      {toggle}
      <span style={{ fontSize: 14, color: theme.colors.text }}>{label}</span>
    </div>
  );
}

function SettingsPage(props: {
  subscriptionStatus: SubscriptionStatus;
  daysUntilExpiry: number | null;
  onResubscribe: () => void;
}) {
  const auth = useMemo(() => getAuth(), []);
  const idToken = auth.idToken!;

  const [initial, setInitial] = useState<UserSettings | null>(null);

  useEffect(() => {
    getSettings(idToken)
      .then(r => setInitial(r.settings))
      .catch(() => setInitial({} as UserSettings));
  }, [idToken]);

  if (!initial) return null;

  return (
    <div style={{ display: 'grid', gap: 18 }}>
      <ScheduleSettingsCard
        idToken={idToken}
        initial={initial}
        subscriptionStatus={props.subscriptionStatus}
        daysUntilExpiry={props.daysUntilExpiry}
        onResubscribe={props.onResubscribe}
      />
      <TrustedContactSettingsCard idToken={idToken} initial={initial} />
      <DeleteAccountCard idToken={idToken} />
    </div>
  );
}

function DeleteAccountCard({ idToken }: { idToken: string }) {
  const [confirming, setConfirming] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onDelete() {
    if (busy || confirmText !== 'delete') return;
    setBusy(true);
    setError(null);
    try {
      await deleteAccount({ idToken });
      clearAuth();
      window.location.replace('/');
    } catch (e: any) {
      setError(e?.message ?? 'failed');
      setBusy(false);
    }
  }

  return (
    <>
      <Card>
        <CardTitle>Delete account</CardTitle>
        <Muted style={{ marginTop: 4 }}>
          Permanently erase your account, all your messages, and any uploaded files. This action cannot be undone.
        </Muted>
        <Hr />
        <ButtonRow>
          <Button
            variant="danger"
            onClick={() => { setConfirming(true); setConfirmText(''); setError(null); }}
          >
            Delete account
          </Button>
        </ButtonRow>
      </Card>

      {confirming && (
        <Overlay>
          <Modal style={{ maxWidth: 420 }}>
            <CardTitle>Delete account</CardTitle>
            <Muted style={{ marginTop: 4 }}>
              This will permanently delete your account, every message you've set up, and any uploaded files. Your trusted contact will no longer receive anything from you. This action cannot be undone.
            </Muted>
            <Hr />
            <Field>
              <Label>Type <b>delete</b> to confirm</Label>
              <Input
                value={confirmText}
                onChange={e => setConfirmText(e.target.value)}
                placeholder="delete"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                onKeyDown={e => { if (e.key === 'Enter' && confirmText === 'delete') onDelete(); }}
              />
            </Field>
            {error && <Muted style={{ color: '#ff8898', marginTop: 6 }}>Couldn't delete: {error}</Muted>}
            <Hr />
            <ButtonRow>
              <Button variant="ghost" onClick={() => { setConfirming(false); setConfirmText(''); }} disabled={busy}>Cancel</Button>
              <div style={{ flex: 1 }} />
              <Button
                onClick={onDelete}
                disabled={confirmText !== 'delete' || busy}
                style={{ background: '#b83352', borderColor: '#b83352', color: '#fff' }}
              >
                {busy ? 'Deleting…' : 'Delete account'}
              </Button>
            </ButtonRow>
          </Modal>
        </Overlay>
      )}
    </>
  );
}

function ScheduleSettingsCard({ idToken, initial, subscriptionStatus, daysUntilExpiry, onResubscribe }: { idToken: string; initial: UserSettings; subscriptionStatus: SubscriptionStatus; daysUntilExpiry: number | null; onResubscribe: () => void }) {
  const [checkinInterval, setCheckinInterval] = useState<Interval>(initial.checkinInterval ?? '1mo');
  const [graceInterval, setGraceInterval] = useState<Interval>(initial.graceInterval ?? '7d');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSave = async () => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      await saveSettings(idToken, { checkinInterval, graceInterval });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card>
      <CardTitle>Check-in schedule</CardTitle>
      <Muted style={{ marginTop: -4 }}>
        This applies to all your messages. If you miss a check-in and the grace period passes, your messages will be sent.
      </Muted>
      <Hr />
      {subscriptionStatus !== 'active' && (
        <SubscriptionAlert
          status={subscriptionStatus}
          daysUntilExpiry={daysUntilExpiry}
          onResubscribe={onResubscribe}
          style={{ marginBottom: 14 }}
        />
      )}
      <Field>
        <Label>Check in every</Label>
        <Segmented value={checkinInterval} onChange={setCheckinInterval} />
      </Field>
      <Field>
        <Label>Grace period after a missed check-in</Label>
        <Segmented value={graceInterval} onChange={setGraceInterval} />
      </Field>
      <Hr />
      <ButtonRow>
        <Button onClick={onSave} disabled={busy}>
          {busy ? 'Saving…' : saved ? 'Saved' : 'Save schedule'}
        </Button>
      </ButtonRow>
      {error && <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{error}</div>}
    </Card>
  );
}

function TrustedContactSettingsCard({ idToken, initial }: { idToken: string; initial: UserSettings }) {
  // Passphrase is never returned by the API (it's hashed server-side), so the
  // form input always starts blank. We track whether one is already set
  // server-side to swap the placeholder copy and to allow saves that change
  // only phone/question without re-entering the passphrase.
  const initialContact = initial.trustedContact ?? null;
  const [phone, setPhone] = useState(initialContact?.phone ?? '');
  const [question, setQuestion] = useState(initialContact?.question ?? '');
  const [passphrase, setPassphrase] = useState('');
  const [hasExisting, setHasExisting] = useState(!!initialContact);
  const [enabled, setEnabled] = useState(!!initialContact);
  const [confirmingEnable, setConfirmingEnable] = useState(false);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onToggle = (v: boolean) => {
    if (v && !enabled) {
      setConfirmingEnable(true);
      return;
    }
    setEnabled(v);
  };

  const onSave = async () => {
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      const payload: TrustedContact | null = enabled
        ? { phone: phone.trim(), question: question.trim(), ...(passphrase ? { passphrase } : {}) }
        : null;
      await saveSettings(idToken, { trustedContact: payload });
      setHasExisting(!!payload);
      setPassphrase(''); // wipe plaintext from memory after a successful save
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err: any) {
      setError(err?.message ?? String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Card>
      <CardTitle>Trusted contact</CardTitle>
      <Muted style={{ marginTop: -4 }}>
        Before your messages are sent, we'll reach out to someone you trust to confirm it's really time.
        If they answer the question right, we'll release the messages.
      </Muted>
      <Hr />

      <Toggle
        checked={enabled}
        onChange={onToggle}
        label="Enable trusted contact verification"
        style={{ marginBottom: enabled ? 16 : 0 }}
      />


      {enabled && (
        <div style={{ display: 'grid', gap: 14 }}>
          <Field>
            <Label>Phone or email</Label>
            <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+1 555 000 0000 or contact@example.com" />
          </Field>
          <Field>
            <Label>What should we ask them?</Label>
            <Input
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. Is it raining in Michigan today?"
            />
          </Field>
          <Field>
            <Label>
              What should they reply with?
              {hasExisting ? <span style={{ color: theme.colors.muted, fontWeight: 400 }}> (set — enter new to change)</span> : null}
            </Label>
            <Input
              type="text"
              value={passphrase}
              onChange={(e) => setPassphrase(e.target.value)}
              placeholder={hasExisting ? 'Leave blank to keep current' : 'A word or phrase only they would know'}
              autoComplete="off"
              spellCheck={false}
              autoCorrect="off"
              autoCapitalize="off"
            />
          </Field>
        </div>
      )}

      <Hr />
      <ButtonRow>
        <Button onClick={onSave} disabled={busy}>
          {busy ? 'Saving…' : saved ? 'Saved' : 'Save contact'}
        </Button>
      </ButtonRow>
      {error && <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{error}</div>}
      </Card>

      {confirmingEnable && (
        <Overlay>
          <Modal style={{ maxWidth: 480 }}>
            <CardTitle>Enable trusted contact?</CardTitle>
            <Muted style={{ marginTop: 4 }}>
              Once on, no message will go out until your trusted contact answers the security question correctly.
              If they don't respond, answer wrong, or can't be reached, your messages will <b>not</b> be delivered —
              even after you've truly missed every check-in.
            </Muted>
            <Muted style={{ marginTop: 8 }}>
              Pick someone you'd trust to confirm something this serious. They'll effectively hold veto power over delivery.
            </Muted>
            <Hr />
            <ButtonRow>
              <Button variant="ghost" onClick={() => setConfirmingEnable(false)}>Cancel</Button>
              <div style={{ flex: 1 }} />
              <Button onClick={() => { setEnabled(true); setConfirmingEnable(false); }}>
                I understand, enable
              </Button>
            </ButtonRow>
          </Modal>
        </Overlay>
      )}
    </>
  );
}

function PlaceholderPage({ title }: { title: string }) {
  return (
    <Card>
      <CardTitle>{title}</CardTitle>
      <Muted>Not wired yet.</Muted>
    </Card>
  );
}

function OtpCodeField(props: { value: string; onChange: (v: string) => void; onComplete?: () => void; length?: number; disabled?: boolean }) {
  const length = props.length ?? 6;
  const digits = Array.from({ length }, (_, i) => (props.value[i] ?? ''));
  const refs = useRef<Array<HTMLInputElement | null>>([]);
  const [focusedIdx, setFocusedIdx] = useState<number | null>(null);

  useEffect(() => {
    refs.current[0]?.focus();
  }, []);

  const setAt = (idx: number, ch: string) => {
    const next = digits.slice();
    next[idx] = ch;
    props.onChange(next.join(''));
  };

  const focus = (idx: number) => {
    const el = refs.current[idx];
    if (el) el.focus();
  };

  const onPaste = (e: React.ClipboardEvent) => {
    const text = (e.clipboardData.getData('text') ?? '').replace(/\D/g, '').slice(0, length);
    if (!text) return;
    e.preventDefault();
    const next = Array.from({ length }, (_, i) => text[i] ?? '');
    props.onChange(next.join(''));
    const last = Math.min(text.length, length) - 1;
    if (last >= 0) focus(last);
    if (text.length >= length) setTimeout(() => props.onComplete?.(), 0);
  };

  return (
    <div style={{ display: 'flex', gap: 10 }} onPaste={onPaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          value={d}
          disabled={props.disabled}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          onChange={(e) => {
            const raw = e.target.value;
            const ch = raw.replace(/\D/g, '').slice(-1);
            setAt(i, ch);
            if (ch && i < length - 1) focus(i + 1);
            if (ch && i === length - 1) setTimeout(() => props.onComplete?.(), 0);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              if (digits[i]) {
                setAt(i, '');
              } else if (i > 0) {
                setAt(i - 1, '');
                focus(i - 1);
              }
              e.preventDefault();
              return;
            }
            if (e.key === 'ArrowLeft' && i > 0) {
              focus(i - 1);
              e.preventDefault();
              return;
            }
            if (e.key === 'ArrowRight' && i < length - 1) {
              focus(i + 1);
              e.preventDefault();
              return;
            }
          }}
          onFocus={(e) => { e.currentTarget.select(); setFocusedIdx(i); }}
          onBlur={() => setFocusedIdx(null)}
          style={{
            width: 44,
            height: 52,
            borderRadius: 12,
            border: `1px solid ${focusedIdx === i ? 'rgba(210,180,91,0.55)' : theme.colors.line}`,
            background: focusedIdx === i ? 'rgba(210,180,91,0.07)' : 'rgba(255,255,255,0.04)',
            boxShadow: focusedIdx === i ? '0 0 0 3px rgba(210,180,91,0.12)' : 'none',
            color: theme.colors.text,
            textAlign: 'center',
            fontSize: 22,
            fontWeight: 900,
            outline: 'none',
            transition: 'border-color 150ms, background 150ms, box-shadow 150ms',
          }}
        />
      ))}
    </div>
  );
}

function isValidEmail(v: string) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<'email' | 'sending' | 'code'>('email');
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [session, setSession] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSendCode = async () => {
    const e = email.trim().toLowerCase();
    if (!isValidEmail(e)) {
      setError('Enter a valid email address');
      return;
    }
    setBusy(true);
    setError(null);
    setStep('sending');
    try {
      const r = await authOtpStart(e);
      setSession(r.session);
      setStep('code');
    } catch (err: any) {
      setError((err?.message ?? String(err)).replace(/\.$/, ''));
      setStep('email');
    } finally {
      setBusy(false);
    }
  };

  const onVerify = async (overrideCode?: string) => {
    if (busy || !session) return;
    const e = email.trim().toLowerCase();
    const c = (overrideCode ?? code).replace(/\D/g, '').trim();
    if (!e || c.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const r = await authOtpVerify({ email: e, code: c, session });
      setAuth(r.idToken, e, r.refreshToken ?? null);
      track('login_success');
      onSignedIn();
      navigate('/dashboard');
    } catch (err: any) {
      setError((err?.message ?? String(err)).replace(/\.$/, ''));
    } finally {
      setBusy(false);
    }
  };

  const canVerify = code.replace(/\D/g, '').length === 6;

  const lastAutoVerify = useRef<string | null>(null);
  useEffect(() => {
    if (step !== 'code') return;
    if (!session) return;
    if (busy) return;
    const c = code.replace(/\D/g, '').trim();
    if (c.length !== 6) return;
    if (lastAutoVerify.current === c) return;
    lastAutoVerify.current = c;
    // Auto-submit once 6 digits are present (covers paste + manual entry reliably).
    Promise.resolve().then(() => onVerify(c));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, step, session, busy]);

  return (
    <div style={{ display: 'grid', placeItems: 'center', minHeight: '65vh' }}>
      <div style={{ width: 'min(520px, 100%)' }}>
        <Card>
          <CardTitle>Log in</CardTitle>
          <Muted style={{ marginTop: -4 }}>New here? We'll set up your account automatically.</Muted>
          <Hr />

          {step === 'email' && (
            <>
              <Field>
                <Label>Email</Label>
                <Input
                  value={email}
                  onChange={(e) => { setEmail(e.target.value); if (error) setError(null); }}
                  onKeyDown={(e) => e.key === 'Enter' && onSendCode()}
                  placeholder="example@domain.com"
                  autoFocus
                />
                {error ? (
                  <div style={{ marginTop: 8, color: theme.colors.danger, fontSize: 12 }}>{error}</div>
                ) : null}
              </Field>
              <ButtonRow>
                <Button onClick={onSendCode}>Send code</Button>
              </ButtonRow>
            </>
          )}

          {step === 'sending' && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '16px 0' }}>
              <Spinner size={22} />
            </div>
          )}

          {step === 'code' && (
            <>
              <Field>
                <Label>6-digit code</Label>
                <OtpCodeField
                  value={code}
                  onChange={(v) => { setCode(v); if (error) setError(null); }}
                  disabled={busy}
                  onComplete={onVerify}
                />
                {error ? (
                  <div style={{ marginTop: 8, color: theme.colors.danger, fontSize: 12 }}>{error}</div>
                ) : (
                  <Muted style={{ marginTop: 10, fontSize: 12 }}>Tip: paste the full code and it will auto-fill.</Muted>
                )}
              </Field>
              <ButtonRow>
                <Button onClick={() => onVerify()} disabled={!canVerify || busy}>Verify</Button>
                {busy && <Spinner size={16} />}
              </ButtonRow>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}


function fmtRelative(targetSec: number | null | undefined) {
  if (!targetSec || typeof targetSec !== 'number') return '—';
  const now = Math.floor(Date.now() / 1000);
  const delta = targetSec - now;
  const abs = Math.abs(delta);
  const days = Math.floor(abs / (24 * 60 * 60));
  const hours = Math.floor((abs % (24 * 60 * 60)) / (60 * 60));

  const unit = days >= 2 ? `${days} days` : days === 1 ? '1 day' : hours >= 2 ? `${hours} hours` : hours === 1 ? '1 hour' : 'moments';
  return delta >= 0 ? `in ${unit}` : `${unit} ago`;
}

function accountTimerPill(settings: UserSettings | null) {
  const now = Math.floor(Date.now() / 1000);
  const dueAt = typeof settings?.dueAt === 'number' ? settings.dueAt : null;

  const green = '#46d37a';
  const greenBorder = '#2fb85f';
  const gold = theme.colors.gold;
  const goldBorder = '#9a8146';
  const red = theme.colors.danger;
  const redBorder = '#b63a3a';

  if (!dueAt) return null;

  const remainingDays = (dueAt - now) / (24 * 60 * 60);
  if (remainingDays <= 0) return { bg: red, border: redBorder, text: 'Overdue' };
  if (remainingDays <= 5) return { bg: gold, border: goldBorder, text: 'Due soon' };
  return { bg: green, border: greenBorder, text: 'OK' };
}



function DashboardPage({ trippedAt, hasDevice, subscriptionStatus, daysUntilExpiry, onResubscribe, onOpenCreate, messagesVersion }: { trippedAt?: number | null; hasDevice?: boolean; subscriptionStatus: SubscriptionStatus; daysUntilExpiry: number | null; onResubscribe: () => void; onOpenCreate: () => void; messagesVersion: number }) {
  const auth = useMemo(() => getAuth(), []);
  const idToken = auth.idToken;

  const [messages, setMessages] = useState<any[]>([]);
  const [settings, setSettings] = useState<UserSettings | null>(null);
  // Split per concern so a settings refresh (e.g. on check-in) doesn't make
  // the messages list flicker through its skeleton loader.
  const [messagesBusy, setMessagesBusy] = useState(false);
  const [messagesCursor, setMessagesCursor] = useState<string | null>(null);
  const [moreBusy, setMoreBusy] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ messageId: string; recipientEmail: string } | null>(null);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deletedRecipient, setDeletedRecipient] = useState<string | null>(null);
  const [imOkayKey, setImOkayKey] = useState(0);
  // Three-stage check-in animation:
  //   1. fading (t=1700ms): MiniStat opacity:0 transitions in sync with ImOkayButton
  //   2. collapsed (t=2200ms): unmount the now-invisible button + ministat so
  //      the card doesn't keep their reserved height while we wait for new
  //      settings — otherwise the card looks like it has empty padding
  //   3. loadSettings() resolves: reset both states alongside new settings
  const [checkinFading, setCheckinFading] = useState(false);
  const [checkinCollapsed, setCheckinCollapsed] = useState(false);
  const [appBannerDismissed, setAppBannerDismissed] = useState(
    () => localStorage.getItem(APP_BANNER_DISMISSED_KEY) === '1'
  );
  const dismissAppBanner = () => {
    localStorage.setItem(APP_BANNER_DISMISSED_KEY, '1');
    setAppBannerDismissed(true);
  };
  // Show the download nudge only to users who haven't installed the app (no
  // registered push device) and haven't already waved it off. Hidden once the
  // switch has tripped — that screen is terminal.
  const showAppBanner = !hasDevice && !appBannerDismissed && !trippedAt;

  const loadMessages = async () => {
    if (!idToken) return;
    setMessagesBusy(true);
    try {
      const r = await listMessages(idToken);
      setMessages(r.items);
      setMessagesCursor(r.nextCursor ?? null);
    } finally {
      setMessagesBusy(false);
    }
  };

  const loadMoreMessages = async () => {
    if (!idToken || !messagesCursor || moreBusy) return;
    setMoreBusy(true);
    try {
      const r = await listMessages(idToken, messagesCursor);
      setMessages((prev) => {
        const seen = new Set(prev.map((m) => m.messageId));
        return [...prev, ...r.items.filter((m) => !seen.has(m.messageId))];
      });
      setMessagesCursor(r.nextCursor ?? null);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setMoreBusy(false);
    }
  };

  const loadSettings = async () => {
    if (!idToken) return;
    setSettingsBusy(true);
    try {
      const r = await getSettings(idToken);
      setSettings(r.settings);
    } finally {
      setSettingsBusy(false);
    }
  };

  const loadAll = async () => {
    if (!idToken) return;
    setMessagesBusy(true);
    setSettingsBusy(true);
    try {
      const [msgRes, settingsRes] = await Promise.all([
        listMessages(idToken),
        getSettings(idToken),
      ]);
      setMessages(msgRes.items);
      setMessagesCursor(msgRes.nextCursor ?? null);
      setSettings(settingsRes.settings);
    } finally {
      setMessagesBusy(false);
      setSettingsBusy(false);
    }
  };

  const onImOkayConfirm = () => {
    if (!idToken) return;
    window.setTimeout(() => setCheckinFading(true), 1700);
    window.setTimeout(() => setCheckinCollapsed(true), 2200);
    checkin(idToken)
      .then(() => {
        track('checkin');
        window.setTimeout(async () => {
          await loadSettings();
          setCheckinFading(false);
          setCheckinCollapsed(false);
        }, 2400);
      })
      .catch((e: any) => {
        setError(e?.message ?? String(e));
        window.setTimeout(() => {
          setImOkayKey(k => k + 1);
          setCheckinFading(false);
          setCheckinCollapsed(false);
        }, 2400);
      });
  };

  const onDelete = async () => {
    if (!idToken || deleteBusy || !deleteTarget) return;
    setDeleteBusy(true);
    const recipient = deleteTarget.recipientEmail;
    try {
      await deleteMessage({ idToken, messageId: deleteTarget.messageId });
      track('message_delete');
      await loadMessages();
      setDeleteTarget(null);
      setDeleteConfirmText('');
      setDeletedRecipient(recipient);
    } catch (e: any) {
      setError(e?.message ?? String(e));
    } finally {
      setDeleteBusy(false);
    }
  };

  useEffect(() => {
    loadAll().catch((e) => setError(String(e.message ?? e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh just the message list when the parent signals a successful create.
  // Skip on initial mount (handled by loadAll above).
  useEffect(() => {
    if (messagesVersion === 0) return;
    loadMessages().catch((e) => setError(String(e.message ?? e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messagesVersion]);

  return (
    <>
      {deletedRecipient && (
        <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) setDeletedRecipient(null); }}>
          <Modal style={{ maxWidth: 420 }}>
            <div style={{ display: 'flex', justifyContent: 'center', padding: '12px 0 8px' }}>
              <SuccessCheck size={72} />
            </div>
            <CardTitle style={{ textAlign: 'center', margin: 0 }}>Deleted</CardTitle>
            <Muted style={{ textAlign: 'center', marginTop: 6 }}>
              Your message to <b>{deletedRecipient}</b> is gone.
            </Muted>
            <Hr />
            <ButtonRow>
              <div style={{ flex: 1 }} />
              <Button onClick={() => setDeletedRecipient(null)}>Done</Button>
            </ButtonRow>
          </Modal>
        </Overlay>
      )}

      {deleteTarget && (
        <Overlay>
          <Modal style={{ maxWidth: 420 }}>
            <CardTitle>Delete message</CardTitle>
            <Muted style={{ marginTop: 4 }}>
              This will permanently delete the message to <b>{deleteTarget.recipientEmail}</b>. This action cannot be undone.
            </Muted>
            <Hr />
            <Field>
              <Label>Type <b>delete</b> to confirm</Label>
              <Input
                value={deleteConfirmText}
                onChange={e => setDeleteConfirmText(e.target.value)}
                placeholder="delete"
                autoFocus
                autoComplete="off"
                autoCorrect="off"
                autoCapitalize="off"
                spellCheck={false}
                onKeyDown={e => { if (e.key === 'Enter' && deleteConfirmText === 'delete') onDelete(); }}
              />
            </Field>
            <Hr />
            <ButtonRow>
              <Button variant="ghost" onClick={() => { setDeleteTarget(null); setDeleteConfirmText(''); }} disabled={deleteBusy}>Cancel</Button>
              <div style={{ flex: 1 }} />
              <Button
                onClick={onDelete}
                disabled={deleteConfirmText !== 'delete' || deleteBusy}
                style={{ background: '#b83352', borderColor: '#b83352', color: '#fff' }}
              >
                {deleteBusy ? 'Deleting…' : 'Delete'}
              </Button>
            </ButtonRow>
          </Modal>
        </Overlay>
      )}

      {showAppBanner && <AppDownloadBanner onDismiss={dismissAppBanner} />}

      {trippedAt && (
        <Card style={{
          border: '1px solid rgba(200,173,106,0.4)',
          boxShadow: '0 0 24px rgba(200,173,106,0.10)',
          background: 'rgba(200,173,106,0.06)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <SealedEnvelopeIcon />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>Your messages have been sent</div>
              <div style={{ fontSize: 12, color: 'rgba(233,237,243,0.55)', marginTop: 3 }}>
                Triggered {fmtRelative(trippedAt)}
              </div>
            </div>
          </div>
        </Card>
      )}

      {(() => {
        const now = Math.floor(Date.now() / 1000);
        const timerPill = accountTimerPill(settings);
        const checkinMissed = typeof settings?.dueAt === 'number' && settings.dueAt < now;
        const graceActive = typeof settings?.graceEndsAt === 'number' && settings.graceEndsAt > 0;
        const needsAttention = timerPill && (timerPill.text === 'Due soon' || timerPill.text === 'Overdue');

        const thresholdSec = (() => {
          switch (settings?.checkinInterval) {
            case '7d': return 3 * 86400;
            case '3mo': return 14 * 86400;
            case '1mo':
            default: return 7 * 86400;
          }
        })();
        const dueAt = typeof settings?.dueAt === 'number' ? settings.dueAt : null;
        // Unbounded on the right: red must not expire when the grace window
        // does. Past graceEndsAt the account is either about to dispatch or has
        // stopped being acted on — neither is the calm state.
        const inGrace = dueAt !== null && now >= dueAt;
        const showCheckin = dueAt !== null && (inGrace || (dueAt - now) <= thresholdSec);

        const awaitingConfirmation = !!settings?.awaitingConfirmationAt;

        return (
          <Card>
            {(awaitingConfirmation || inGrace || error) && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                {awaitingConfirmation ? (
                  <span style={{ color: theme.colors.danger, fontSize: 13, fontWeight: 700, margin: 0 }}>
                    Awaiting trusted contact verification — check in to cancel.
                  </span>
                ) : inGrace ? (
                  <span style={{ color: theme.colors.danger, fontSize: 13, fontWeight: 700, margin: 0 }}>
                    You missed your check-in. Let us know if you're okay.
                  </span>
                ) : null}
                {error ? <span style={{ color: theme.colors.danger, fontSize: 13 }}>{error}</span> : null}
              </div>
            )}

            <div style={{ marginTop: (awaitingConfirmation || inGrace || error) ? 12 : 0 }}>
              <Card style={{ background: 'transparent', border: 'none', boxShadow: 'none' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, minHeight: 64 }}>
                  {settingsBusy || !settings ? (
                    <Spinner size={32} />
                  ) : (
                    <>
                      <NextCheckInPulse dueAt={settings?.dueAt ?? null} graceEndsAt={settings?.graceEndsAt ?? null} awaiting={awaitingConfirmation} />
                      {showCheckin && !checkinCollapsed && <ImOkayButton key={imOkayKey} disabled={!idToken} onConfirm={onImOkayConfirm} />}
                    </>
                  )}
                </div>
              </Card>
            </div>
            {needsAttention && !checkinCollapsed && (
              <MobileHide active={awaitingConfirmation} style={{
                marginTop: 12,
                display: 'grid',
                gridTemplateColumns: graceActive ? '1fr 1fr' : '1fr',
                gap: 8,
                opacity: checkinFading ? 0 : 1,
                transition: 'opacity 0.45s ease-out',
                pointerEvents: checkinFading ? 'none' : undefined,
              }}>
                <MiniStat
                  label={checkinMissed ? 'Missed check-in' : 'Next check-in'}
                  value={fmtRelative(settings!.dueAt)}
                  valueColor={checkinMissed ? theme.colors.danger : undefined}
                  borderColor={checkinMissed ? 'rgba(184,58,58,0.5)' : undefined}
                />
                {graceActive && (
                  <MiniStat
                    label="Grace ends"
                    value={fmtRelative(settings!.graceEndsAt)}
                    valueColor={(settings!.graceEndsAt! < now) ? theme.colors.danger : undefined}
                    borderColor={(settings!.graceEndsAt! < now) ? 'rgba(184,58,58,0.5)' : undefined}
                  />
                )}
              </MobileHide>
            )}
          </Card>
        );
      })()}

      {(() => {
        const yourMessages = messagesBusy ? null : [...messages].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0));

        if (!messagesBusy && messages.length > 0 && yourMessages?.length === 0) return null;

        return (
          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CardTitle style={{ margin: 0 }}>Your messages</CardTitle>
                {messagesBusy && <Spinner size={14} />}
              </div>
              <Button onClick={onOpenCreate}>Create a message</Button>
            </div>
            <Hr style={{ margin: '0 0 14px' }} />

            {subscriptionStatus !== 'active' && (subscriptionStatus !== 'free' || messages.length >= 1) && (
              <SubscriptionAlert
                status={subscriptionStatus}
                daysUntilExpiry={daysUntilExpiry}
                onResubscribe={onResubscribe}
                style={{ marginBottom: 14 }}
              />
            )}

            {messagesBusy ? (
              <div style={{ display: 'grid', gap: 10 }}>
                <style>{`
                  @keyframes skeleton-shimmer {
                    0% { background-position: -400px 0; }
                    100% { background-position: 400px 0; }
                  }
                `}</style>
                {[0, 1, 2].map((i) => (
                  <div
                    key={i}
                    style={{
                      height: 62,
                      borderRadius: 14,
                      border: `1px solid ${theme.colors.line}`,
                      backgroundSize: '800px 100%',
                      backgroundImage: `linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)`,
                      animation: 'skeleton-shimmer 1.4s ease-in-out infinite',
                    }}
                  />
                ))}
              </div>
            ) : messages.length === 0 ? (
              <Muted>No messages yet.</Muted>
            ) : (
              <div style={{ display: 'grid', gap: 12 }}>
                {yourMessages!.map((m) => {
                  const sizeBytes = typeof m.totalAttachmentBytes === 'number' ? m.totalAttachmentBytes : null;
                  return (
                    <Card
                      key={m.messageId}
                      style={{ padding: 14, background: 'rgba(0,0,0,0.12)' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {m.recipientEmail}
                          </div>
                          <div style={{ marginTop: 4, display: 'flex', gap: 10, fontSize: 12, color: 'rgba(233,237,243,0.4)' }}>
                            {m.createdAt && <span>{fmtRelative(m.createdAt)}</span>}
                            {sizeBytes !== null && <span>{formatBytes(sizeBytes)}</span>}
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          <button
                            onClick={() => { setDeleteTarget({ messageId: m.messageId, recipientEmail: m.recipientEmail }); setDeleteConfirmText(''); }}
                            style={{
                              background: 'none', border: 'none', cursor: 'pointer',
                              color: 'rgba(233,237,243,0.35)',
                              padding: 6, borderRadius: 6,
                              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                              transition: 'color 0.15s',
                            }}
                            onMouseEnter={e => (e.currentTarget.style.color = 'rgba(233,237,243,0.75)')}
                            onMouseLeave={e => (e.currentTarget.style.color = 'rgba(233,237,243,0.35)')}
                            title="Delete"
                            aria-label="Delete message"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                              <path d="M3 6h18" />
                              <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                              <path d="M10 11v6" />
                              <path d="M14 11v6" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            )}

            {!messagesBusy && messagesCursor && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: 14 }}>
                <button
                  onClick={loadMoreMessages}
                  disabled={moreBusy}
                  style={{
                    background: 'none', border: 'none', padding: 4,
                    font: 'inherit', fontSize: 13,
                    color: theme.colors.gold,
                    cursor: moreBusy ? 'wait' : 'pointer',
                    textDecoration: 'underline', textUnderlineOffset: 3,
                    display: 'inline-flex', alignItems: 'center', gap: 8,
                  }}
                >
                  {moreBusy && <Spinner size={12} />}
                  Load more
                </button>
              </div>
            )}
          </Card>
        );
      })()}
    </>
  );
}

function SealedEnvelopeIcon() {
  const w = 80, h = 36;
  const gold = theme.colors.gold;
  const dim = 'rgba(255,255,255,0.10)';
  return (
    <div style={{ position: 'relative', width: w, height: h, flexShrink: 0 }}>
      {/* Body */}
      <div style={{
        position: 'absolute', inset: 0,
        borderRadius: 7,
        border: `1px solid ${dim}`,
        background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.18))'
      }} />
      {/* Closed flap */}
      <div style={{
        position: 'absolute', left: 0, right: 0, top: 0,
        height: 23,
        transformOrigin: 'top center',
        transform: 'rotateX(0deg)',
        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
        background: 'linear-gradient(180deg, rgba(200,173,106,0.22), rgba(200,173,106,0.06))',
        borderTopLeftRadius: 7,
        borderTopRightRadius: 7,
      }} />
      {/* Inner V shadow */}
      <div style={{
        position: 'absolute', left: 0, right: 0, bottom: 0,
        height: 21,
        clipPath: 'polygon(0 100%, 100% 100%, 50% 0)',
        background: 'rgba(255,255,255,0.04)'
      }} />
      {/* Gold seal line */}
      <div style={{
        position: 'absolute', left: 7, right: 7, top: 15,
        height: 1,
        background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
        opacity: 1,
      }} />
      {/* Wax seal */}
      <div style={{
        position: 'absolute',
        left: w / 2 - 11, top: h / 2 - 11,
        width: 22, height: 22,
        borderRadius: '50%',
        overflow: 'hidden',
        border: '1px solid rgba(200,173,106,0.75)',
        boxShadow: '0 0 7px rgba(200,173,106,0.35), inset 0 0 4px rgba(0,0,0,0.4)',
        zIndex: 4,
      }}>
        <img src="/logo.png" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      </div>
    </div>
  );
}

function MiniStat({ label, value, valueColor, borderColor }: { label: string; value: any; valueColor?: string; borderColor?: string }) {
  const v = value == null ? '—' : String(value);
  return (
    <div
      style={{
        padding: 10,
        borderRadius: 12,
        border: `1px solid ${borderColor ?? theme.colors.line}`,
        background: 'rgba(0,0,0,0.10)'
      }}
    >
      <div style={{ fontSize: 11, color: theme.colors.muted }}>{label}</div>
      <div style={{ fontSize: 13, marginTop: 4, color: valueColor ?? theme.colors.text }}>{v}</div>
    </div>
  );
}



function Segmented({ value, onChange }: { value: Interval; onChange: (v: Interval) => void }) {
  return (
    <ButtonRow>
      <Button variant={value === '7d' ? 'primary' : 'ghost'} onClick={() => onChange('7d')}>
        7 days
      </Button>
      <Button variant={value === '1mo' ? 'primary' : 'ghost'} onClick={() => onChange('1mo')}>
        1 month
      </Button>
      <Button variant={value === '3mo' ? 'primary' : 'ghost'} onClick={() => onChange('3mo')}>
        3 months
      </Button>
    </ButtonRow>
  );
}

function OnboardingModal({ idToken, onDone }: { idToken: string; onDone: () => void }) {
  const [checkinInterval, setCheckinInterval] = useState<Interval>('1mo');
  const [graceInterval, setGraceInterval] = useState<Interval>('7d');
  const [busy, setBusy] = useState(false);
  const [visible, setVisible] = useState(true);

  const onConfirm = async () => {
    setBusy(true);
    try {
      await saveSettings(idToken, { checkinInterval, graceInterval });
      setVisible(false);
      setTimeout(onDone, 400);
    } catch {
      setBusy(false);
    }
  };

  return (
    <Overlay style={{ transition: 'opacity 0.4s', opacity: visible ? 1 : 0 }}>
      <Modal>
        <StepHeader>
          <StepTitle>Set up your schedule</StepTitle>
          <StepPills>
            <ProgressWrap><ProgressBar pct={100} /></ProgressWrap>
          </StepPills>
        </StepHeader>

        <Muted style={{ marginBottom: 20, lineHeight: 1.6 }}>
          SilentPost checks in on you periodically. If you go silent and miss a check-in, your messages are held for a grace period — then sent. Pick what feels right.
        </Muted>

        <Field>
          <Label>Check in every</Label>
          <Segmented value={checkinInterval} onChange={setCheckinInterval} />
        </Field>

        <Field>
          <Label>Grace period after a missed check-in</Label>
          <Segmented value={graceInterval} onChange={setGraceInterval} />
        </Field>

        <Muted style={{ marginTop: 16, fontSize: 12, opacity: 0.6 }}>
          You can change these at any time in Settings.
        </Muted>

        <Hr />
        <ButtonRow>
          <div style={{ flex: 1 }} />
          <Button onClick={onConfirm} disabled={busy}>
            {busy ? 'Saving…' : "Let's go"}
          </Button>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

function CreateFlowOverlay({ idToken, onClose }: { idToken: string; onClose: (created: boolean) => void }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(false);
  const [phase, setPhase] = useState<'idle' | 'sending' | 'closing' | 'done'>('idle');
  const [err, setErr] = useState<string | null>(null);

  const [recipientEmail, setRecipientEmail] = useState('');
  const [plaintext, setPlaintext] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [storage, setStorage] = useState<{ used: number; limit: number } | null>(null);

  const steps = ['recipient', 'content', 'review', 'create'];
  const hasContent = plaintext.trim().length > 0 || files.length > 0;
  const EMPTY_CONTENT_ERROR = 'A message has to contain either text or a file to be created';
  const INVALID_EMAIL_ERROR = 'Enter a valid email address.';
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  const emailValid = EMAIL_RE.test(recipientEmail.trim());
  const MAX_FILE_BYTES = 5 * 1024 * 1024 * 1024; // 5 GB — also the S3 single-PUT cap.

  // Pull current storage usage so we can reject files client-side that would
  // exceed the account quota. Server is still authoritative; this is just to
  // avoid letting the user upload 4 GB only to be told no at commit time.
  useEffect(() => {
    let cancelled = false;
    getProfile(idToken)
      .then(r => { if (!cancelled) setStorage({ used: r.profile.storageUsedBytes, limit: r.profile.storageLimitBytes }); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [idToken]);

  // Clear input-driven errors as the underlying field becomes valid, so the
  // message disappears on the first keystroke instead of lingering.
  useEffect(() => {
    if (hasContent) setErr((e) => (e === EMPTY_CONTENT_ERROR ? null : e));
  }, [hasContent]);
  useEffect(() => {
    if (emailValid) setErr((e) => (e === INVALID_EMAIL_ERROR ? null : e));
  }, [emailValid]);

  const goNext = () => {
    if (busy) return;
    if (step === 0) {
      if (!emailValid) { setErr(INVALID_EMAIL_ERROR); return; }
      setStep(1);
      return;
    }
    if (step === 1) {
      if (!hasContent) { setErr(EMPTY_CONTENT_ERROR); return; }
      setStep(2);
      return;
    }
  };

  const close = () => {
    onClose(created);
  };

  const submit = async () => {
    if (busy || created) return;
    if (!hasContent) {
      setErr(EMPTY_CONTENT_ERROR);
      return;
    }
    setBusy(true);
    setPhase('sending');
    setErr(null);
    setStep(3);

    try {
      // init/upload/commit handles text-only, files-only, and text+files.
      // Stays consistent so we don't carry two server paths.
      const init = await initMessage({
        idToken,
        recipientEmail,
        plaintext,
        attachments: files.map((f) => ({
          name: f.name,
          mime: f.type || 'application/octet-stream',
          size: f.size,
          kind: f.type.startsWith('image/') ? 'image' : f.type.startsWith('video/') ? 'video' : 'file'
        }))
      });

      // Upload sequentially (simpler + easier progress). Can parallelize later.
      let uploaded = 0;
      for (const u of init.uploadUrls) {
        const file = files.find((f) => f.name === u.name && f.size === u.size) ?? files[uploaded];
        await uploadToPresignedUrl({ url: u.url, file, mime: u.mime });
        uploaded += 1;
      }

      await commitMessage({ idToken, messageId: init.messageId });
      track('message_create', { hasFiles: files.length > 0, fileCount: files.length });

      setCreated(true);
      setPhase('closing');
      window.setTimeout(() => {
        setPhase('done');
      }, 580);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
      setPhase('idle');
      setStep(3);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay>
      <Modal>
        <StepHeader>
          <StepTitle>Create a message</StepTitle>
          <StepPills>
            <ProgressWrap>
              <ProgressBar pct={(step / (steps.length - 1)) * 100} />
            </ProgressWrap>
          </StepPills>
        </StepHeader>

        {step === 0 ? (
          <Field>
            <Label>Who should receive it?</Label>
            {/* The recipient is someone else's address, so keep browsers and
                password managers from offering the user's own email.
                Chrome ignores autocomplete="off" on fields it recognises as
                email (type="email", or "email" in name/placeholder), so the
                field is type="text" + inputMode="email" (mobile still gets
                the @ keyboard), with a non-email name and placeholder, plus
                the opt-out attributes 1Password / LastPass / Bitwarden /
                Dashlane honour. */}
            <Input
              type="text"
              inputMode="email"
              name="sp-recipient"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              data-1p-ignore=""
              data-lpignore="true"
              data-bwignore=""
              data-form-type="other"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); goNext(); } }}
              placeholder="their@address.com"
              autoFocus
            />
          </Field>
        ) : null}

        {step === 1 ? (
          <>
            <Field>
              <Label>Your message</Label>
              <Textarea value={plaintext} onChange={(e) => setPlaintext(e.target.value)} placeholder="Write something only they will see..." />
            </Field>
            <Field>
              <Label>Attachments</Label>
              <FileDropzone
                files={files}
                onFiles={(newFiles) => {
                  const key = (f: File) => `${f.name}:${f.size}:${f.lastModified}`;
                  const seen = new Set<string>(files.map(key));
                  const accepted: File[] = [];
                  const skipped: { name: string; reason: 'too_large' | 'over_quota' }[] = [];
                  const limit = storage?.limit ?? Infinity;
                  let runningTotal = (storage?.used ?? 0) + files.reduce((s, f) => s + f.size, 0);
                  for (const f of newFiles) {
                    const k = key(f);
                    if (seen.has(k)) continue;
                    seen.add(k);
                    if (f.size > MAX_FILE_BYTES) {
                      skipped.push({ name: f.name, reason: 'too_large' });
                      continue;
                    }
                    if (runningTotal + f.size > limit) {
                      skipped.push({ name: f.name, reason: 'over_quota' });
                      continue;
                    }
                    runningTotal += f.size;
                    accepted.push(f);
                  }
                  if (accepted.length) setFiles([...files, ...accepted]);
                  if (skipped.length) {
                    const tooLarge = skipped.filter(s => s.reason === 'too_large');
                    const overQuota = skipped.filter(s => s.reason === 'over_quota');
                    const parts: string[] = [];
                    if (tooLarge.length) {
                      parts.push(tooLarge.length === 1
                        ? `${tooLarge[0].name} is larger than 5 GB`
                        : `${tooLarge.length} files larger than 5 GB`);
                    }
                    if (overQuota.length) {
                      const remaining = Math.max(0, limit - runningTotal);
                      const remainingTxt = limit === Infinity ? '' : ` — ${formatBytes(remaining)} left`;
                      parts.push(overQuota.length === 1
                        ? `${overQuota[0].name} exceeds your storage${remainingTxt}`
                        : `${overQuota.length} files exceed your storage${remainingTxt}`);
                    }
                    setErr(parts.join('. ') + '.');
                  } else if (accepted.length) {
                    // A clean drop clears any previous file-rejection error.
                    setErr(null);
                  }
                }}
                onRemove={(idx) => setFiles((xs) => xs.filter((_, i) => i !== idx))}
              />
              {(files.length > 0 || storage) && (
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10 }}>
                  <Muted style={{ fontSize: 12, margin: 0 }}>
                    {files.length > 0 ? `${formatBytes(files.reduce((s, f) => s + f.size, 0))} attached` : null}
                  </Muted>
                  <Muted style={{ fontSize: 12, margin: 0 }}>
                    {storage ? `${formatBytes(Math.max(0, storage.limit - storage.used))} free` : null}
                  </Muted>
                </div>
              )}
            </Field>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <CardTitle>Review</CardTitle>
            <Hr />
            <Muted>
              {"Your message to "}<b>{recipientEmail || "..."}</b>{" will be sent according to your account check-in schedule."}
            </Muted>
            <Hr />

            {plaintext.trim() ? (
              <>
                <Muted style={{ marginBottom: 8 }}>Message preview</Muted>
                <MonoBox>{plaintext}</MonoBox>
              </>
            ) : null}

            {files.length ? (
              <div style={{ marginTop: plaintext.trim() ? 16 : 0 }}>
                <Muted style={{ marginBottom: 8 }}>Files</Muted>
                <Card style={{ padding: 12, background: 'rgba(0,0,0,0.12)' }}>
                  <div style={{ display: 'grid', gap: 8 }}>
                    {files.map((f, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 900, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</div>
                          <Muted style={{ fontSize: 12 }}>{formatBytes(f.size)}</Muted>
                        </div>
                        {simplifyMime(f.type) ? <Muted style={{ fontSize: 12 }}>{simplifyMime(f.type)}</Muted> : null}
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            ) : null}
          </>
        ) : null}

        {step === 3 ? <CreateMagic busy={phase === 'sending'} closing={phase === 'closing'} done={phase === 'done'} full /> : null}

        {err ? <div style={{ marginTop: 12, color: theme.colors.danger, fontSize: 13 }}>{err}</div> : null}

        <Hr />
        <ButtonRow>
          {phase === 'done' ? (
            <>
              <div style={{ flex: 1 }} />
              <Button onClick={close}>Done</Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={close} disabled={busy}>Close</Button>
              <div style={{ flex: 1 }} />
              {step <= 2 ? (
                <>
                  <Button variant="ghost" onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={busy || step === 0}>
                    Back
                  </Button>
                  {step < 2 ? (
                    <Button onClick={goNext} disabled={busy}>
                      Next
                    </Button>
                  ) : (
                    <Button onClick={submit} disabled={busy}>
                      Create
                    </Button>
                  )}
                </>
              ) : null}
            </>
          )}
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

function CreateMagic(props: { busy: boolean; closing: boolean; done: boolean; full?: boolean }) {
  const { busy, closing, done, full } = props;

  const show = busy || closing || done;
  if (!show) return null;

  // Fixed 300px coordinate system so particle pixel positions align with the envelope.
  // Envelope: 160×72, centered → left=70. For full height (150): top=46. Non-full (96): top=12.
  const H = full ? 150 : 96;
  const envL = 70, envW = 160, envH = 72;
  const envT = full ? 46 : 12;
  // Particles start at sides and arc toward the envelope opening (top-center of envelope).
  // Opening coords: x=150 (envL + envW/2), y=envT.
  // L particles at x=18, R at x=282. Average startY ≈ envT+55. dx=±132, dy≈-55.

  const gold = theme.colors.gold;
  const dim = 'rgba(255,255,255,0.10)';
  const isOpen = busy || closing;

  const base = {
    marginTop: 14,
    borderRadius: 16,
    border: `1px solid ${theme.colors.line}`,
    background: 'rgba(0,0,0,0.10)',
    padding: 14
  } as const;

  return (
    <div style={base}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Muted style={{ margin: 0 }}>{done ? 'Created' : closing ? 'Sealing' : 'Creating'}</Muted>
          {!done && <Spinner size={12} />}
        </div>
      </div>

      <div style={{ marginTop: 10, overflow: 'hidden' }}>
        <div style={{ position: 'relative', height: H, width: 300, margin: '0 auto' }}>

          {/* Particles — two-element trick: outer=X linear, inner=Y ease-in-out → smooth arch */}
          {Array.from({ length: 14 }).map((_, i) => {
            const side = i % 2 === 0 ? 'L' : 'R';
            const delay = i * 55;
            const size = 5 + (i % 4);
            const startLeft = side === 'L' ? 18 : 282;
            const startTop = envT + 50 + (i % 4) * 5;
            const hue = 0.85 - (i % 5) * 0.06;
            const dur = closing ? 550 : 1100;
            const fill = closing ? 'forwards' : 'infinite';
            const active = busy || closing;
            return (
              <div
                key={i}
                style={{
                  position: 'absolute',
                  left: startLeft,
                  top: startTop,
                  opacity: active ? undefined : 0,
                  animation: active ? `archX${side} ${dur}ms linear ${delay}ms ${fill}` : 'none',
                }}
              >
                <div style={{
                  width: size,
                  height: size,
                  borderRadius: 999,
                  background: `radial-gradient(circle at 30% 30%, rgba(255,255,255,0.92), rgba(200,173,106,${hue}))`,
                  animation: active ? `archY ${dur}ms ease-in-out ${delay}ms ${fill}` : 'none',
                }} />
              </div>
            );
          })}

          {/* Envelope */}
          <div style={{ position: 'absolute', left: envL, top: envT, width: envW, height: envH, perspective: '280px' }}>
            {/* Body */}
            <div style={{
              position: 'absolute',
              inset: 0,
              borderRadius: 14,
              border: `1px solid ${dim}`,
              background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.18))'
            }} />

            {/* Flap — rotates in 3D: open (folded back) vs closed (sealed) */}
            <div style={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: 46,
              transformOrigin: 'top center',
              transform: isOpen ? 'rotateX(-165deg)' : 'rotateX(0deg)',
              transition: 'transform 320ms cubic-bezier(0.4, 0, 0.2, 1)',
              clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
              background: `linear-gradient(180deg, rgba(200,173,106,0.22), rgba(200,173,106,0.06))`,
              borderTopLeftRadius: 14,
              borderTopRightRadius: 14,
            }} />

            {/* Inner V shadow */}
            <div style={{
              position: 'absolute',
              left: 0, right: 0, bottom: 0,
              height: 42,
              clipPath: 'polygon(0 100%, 100% 100%, 50% 0)',
              background: 'rgba(255,255,255,0.04)'
            }} />

            {/* Gold seal line — brightens when closed */}
            <div style={{
              position: 'absolute',
              left: 14, right: 14, top: 30,
              height: 1,
              background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
              opacity: !isOpen ? 1 : 0.2,
              transition: 'opacity 400ms ease'
            }} />

            {/* Wax seal — stamped onto envelope when done */}
            {done && (
              <div style={{
                position: 'absolute',
                left: envW / 2 - 22, top: envH / 2 - 22,
                width: 44, height: 44,
                borderRadius: '50%',
                overflow: 'hidden',
                border: `1.5px solid rgba(200,173,106,0.75)`,
                boxShadow: `0 0 14px rgba(200,173,106,0.35), inset 0 0 8px rgba(0,0,0,0.4)`,
                animation: 'sealStamp 680ms cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
                zIndex: 4,
              }}>
                <img src="/logo.png" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>
            )}

            {/* Outline comet — fires when closing finishes, lead + 7 trail. */}
            {done && [0,1,2,3,4,5,6,7].map(i => (
              <div key={i} className={`sp-outline-particle sp-ot${i}`} />
            ))}
          </div>

          <style>{`
            @keyframes archXL {
              0%   { transform: translateX(0px);    opacity: 0;    }
              8%   {                                opacity: 0.95; }
              85%  {                                opacity: 0.9;  }
              100% { transform: translateX(132px);  opacity: 0;    }
            }
            @keyframes archXR {
              0%   { transform: translateX(0px);    opacity: 0;    }
              8%   {                                opacity: 0.95; }
              85%  {                                opacity: 0.9;  }
              100% { transform: translateX(-132px); opacity: 0;    }
            }
            @keyframes archY {
              0%   { transform: translateY(0px);   }
              50%  { transform: translateY(-84px); }
              100% { transform: translateY(-57px); }
            }
            @keyframes sealStamp {
              0%   { transform: scale(2.6) translateY(-6px); opacity: 1; }
              50%  { transform: scale(0.88) translateY(2px);             }
              72%  { transform: scale(1.05) translateY(-1px);            }
              100% { transform: scale(1) translateY(0);                  }
            }
            .sp-outline-particle {
              position: absolute;
              left: 0; top: 0;
              border-radius: 50%;
              background: rgba(200,173,106,0.95);
              offset-path: path('M 14 0 L 146 0 Q 160 0 160 14 L 160 58 Q 160 72 146 72 L 14 72 Q 0 72 0 58 L 0 14 Q 0 0 14 0 Z');
              offset-anchor: center;
              opacity: 0;
            }
            .sp-ot0 { width:11px; height:11px; filter:blur(1px);   box-shadow: 0 0 14px 8px rgba(200,173,106,0.46), 0 0 29px 14px rgba(200,173,106,0.15); animation: spo0 1500ms linear 0ms forwards; }
            .sp-ot1 { width:10px; height:10px; filter:blur(1.5px); box-shadow: 0 0 12px 6px rgba(200,173,106,0.46);  animation: spo1 1500ms linear 25ms  forwards; }
            .sp-ot2 { width:9px;  height:9px;  filter:blur(2px);   box-shadow: 0 0 10px 6px rgba(200,173,106,0.35);  animation: spo2 1500ms linear 50ms  forwards; }
            .sp-ot3 { width:8px;  height:8px;  filter:blur(2.5px); box-shadow: 0 0 9px  5px rgba(200,173,106,0.26);  animation: spo3 1500ms linear 75ms  forwards; }
            .sp-ot4 { width:7px;  height:7px;  filter:blur(3px);   box-shadow: 0 0 7px  4px rgba(200,173,106,0.18);  animation: spo4 1500ms linear 100ms forwards; }
            .sp-ot5 { width:6px;  height:6px;  filter:blur(3.5px); box-shadow: 0 0 6px  3px rgba(200,173,106,0.11);  animation: spo5 1500ms linear 125ms forwards; }
            .sp-ot6 { width:5px;  height:5px;  filter:blur(4px);   box-shadow: 0 0 6px  2px rgba(200,173,106,0.06);  animation: spo6 1500ms linear 150ms forwards; }
            .sp-ot7 { width:4px;  height:4px;  filter:blur(5px);   box-shadow: 0 0 5px  2px rgba(200,173,106,0.03);  animation: spo7 1500ms linear 175ms forwards; }
            @keyframes spo0 { 0% { offset-distance:0%;   opacity:1;    } 90% { opacity:1;    } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo1 { 0% { offset-distance:0%;   opacity:0.72; } 90% { opacity:0.72; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo2 { 0% { offset-distance:0%;   opacity:0.52; } 90% { opacity:0.52; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo3 { 0% { offset-distance:0%;   opacity:0.36; } 90% { opacity:0.36; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo4 { 0% { offset-distance:0%;   opacity:0.24; } 90% { opacity:0.24; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo5 { 0% { offset-distance:0%;   opacity:0.15; } 90% { opacity:0.15; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo6 { 0% { offset-distance:0%;   opacity:0.08; } 90% { opacity:0.08; } 100% { offset-distance:100%; opacity:0; } }
            @keyframes spo7 { 0% { offset-distance:0%;   opacity:0.04; } 90% { opacity:0.04; } 100% { offset-distance:100%; opacity:0; } }
          `}</style>
        </div>
      </div>
    </div>
  );
}

// Redeem-page envelope animation. Two phases driven by the parent:
//   loading — closed envelope + seal stamped + comet looping the outline.
//   opening — seal scales up + fades, then (after the seal clears) the flap
//             rotates open. No particles, no comet during opening.
// Renders bare (no card wrapper) so the parent can avoid card-in-card.
function RedeemAnimation({ phase }: { phase: 'loading' | 'opening' }) {
  // Internal stage for the opening sequence so the flap waits until the
  // seal animation is done. Stages: 0 = unstamping, 1 = flap opening.
  const [stage, setStage] = useState(0);

  useEffect(() => {
    if (phase !== 'opening') { setStage(0); return; }
    const t = window.setTimeout(() => setStage(1), 700);
    return () => window.clearTimeout(t);
  }, [phase]);

  const H = 150;
  const envL = 70, envW = 160, envH = 72, envT = 46;
  const gold = theme.colors.gold;
  const dim = 'rgba(255,255,255,0.10)';
  const isOpening = phase === 'opening';
  const flapOpen = isOpening && stage >= 1;
  const sealVisible = !isOpening || stage === 0;

  return (
    <div style={{ position: 'relative', height: H, width: 300, margin: '24px auto', overflow: 'visible' }}>
      <div style={{ position: 'absolute', left: envL, top: envT, width: envW, height: envH, perspective: '280px' }}>
        {/* Body */}
        <div style={{
          position: 'absolute',
          inset: 0,
          borderRadius: 14,
          border: `1px solid ${dim}`,
          background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(0,0,0,0.18))',
        }} />
        {/* Flap — held closed during loading + opening stage 0; rotates open at stage 1. */}
        <div style={{
          position: 'absolute',
          left: 0, right: 0, top: 0,
          height: 46,
          transformOrigin: 'top center',
          transform: flapOpen ? 'rotateX(-165deg)' : 'rotateX(0deg)',
          transition: 'transform 600ms cubic-bezier(0.4, 0, 0.2, 1)',
          clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
          background: 'linear-gradient(180deg, rgba(200,173,106,0.22), rgba(200,173,106,0.06))',
          borderTopLeftRadius: 14,
          borderTopRightRadius: 14,
        }} />
        {/* Inner V shadow */}
        <div style={{
          position: 'absolute',
          left: 0, right: 0, bottom: 0,
          height: 42,
          clipPath: 'polygon(0 100%, 100% 100%, 50% 0)',
          background: 'rgba(255,255,255,0.04)',
        }} />
        {/* Seal line — fades when flap opens. */}
        <div style={{
          position: 'absolute',
          left: 14, right: 14, top: 30,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${gold}, transparent)`,
          opacity: !flapOpen ? 1 : 0.2,
          transition: 'opacity 400ms ease',
        }} />
        {/* Wax seal — stamped while loading; scales up + fades on opening. */}
        {sealVisible && (
          <div style={{
            position: 'absolute',
            left: envW / 2 - 22, top: envH / 2 - 22,
            width: 44, height: 44,
            borderRadius: '50%',
            overflow: 'hidden',
            border: '1.5px solid rgba(200,173,106,0.75)',
            boxShadow: '0 0 14px rgba(200,173,106,0.35), inset 0 0 8px rgba(0,0,0,0.4)',
            animation: isOpening ? 'sp-seal-unstamp 700ms cubic-bezier(0.4, 0, 0.2, 1) forwards' : undefined,
            zIndex: 4,
          }}>
            <img src="/logo.png" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          </div>
        )}
        {/* Comet — only loops during loading. */}
        {phase === 'loading' && [0,1,2,3,4,5,6,7].map(i => (
          <div
            key={i}
            className={`sp-redeem-outline sp-rt${i}`}
            style={{ animationIterationCount: 'infinite' }}
          />
        ))}
      </div>

      <style>{`
        @keyframes sp-seal-unstamp {
          0%   { transform: scale(1);   opacity: 1; }
          100% { transform: scale(1.7); opacity: 0; }
        }
        .sp-redeem-outline {
          position: absolute;
          left: 0; top: 0;
          border-radius: 50%;
          background: rgba(200,173,106,0.95);
          offset-path: path('M 14 0 L 146 0 Q 160 0 160 14 L 160 58 Q 160 72 146 72 L 14 72 Q 0 72 0 58 L 0 14 Q 0 0 14 0 Z');
          offset-anchor: center;
          opacity: 0;
        }
        .sp-rt0 { width:11px; height:11px; filter:blur(1px);   box-shadow: 0 0 14px 8px rgba(200,173,106,0.46), 0 0 29px 14px rgba(200,173,106,0.15); animation: sp-rto0 1500ms linear 0ms; }
        .sp-rt1 { width:10px; height:10px; filter:blur(1.5px); box-shadow: 0 0 12px 6px rgba(200,173,106,0.46);  animation: sp-rto1 1500ms linear 25ms;  }
        .sp-rt2 { width:9px;  height:9px;  filter:blur(2px);   box-shadow: 0 0 10px 6px rgba(200,173,106,0.35);  animation: sp-rto2 1500ms linear 50ms;  }
        .sp-rt3 { width:8px;  height:8px;  filter:blur(2.5px); box-shadow: 0 0 9px  5px rgba(200,173,106,0.26);  animation: sp-rto3 1500ms linear 75ms;  }
        .sp-rt4 { width:7px;  height:7px;  filter:blur(3px);   box-shadow: 0 0 7px  4px rgba(200,173,106,0.18);  animation: sp-rto4 1500ms linear 100ms; }
        .sp-rt5 { width:6px;  height:6px;  filter:blur(3.5px); box-shadow: 0 0 6px  3px rgba(200,173,106,0.11);  animation: sp-rto5 1500ms linear 125ms; }
        .sp-rt6 { width:5px;  height:5px;  filter:blur(4px);   box-shadow: 0 0 6px  2px rgba(200,173,106,0.06);  animation: sp-rto6 1500ms linear 150ms; }
        .sp-rt7 { width:4px;  height:4px;  filter:blur(5px);   box-shadow: 0 0 5px  2px rgba(200,173,106,0.03);  animation: sp-rto7 1500ms linear 175ms; }
        @keyframes sp-rto0 { 0% { offset-distance:0%; opacity:1;    } 50% { opacity:1;    } 100% { offset-distance:100%; opacity:1;    } }
        @keyframes sp-rto1 { 0% { offset-distance:0%; opacity:0.72; } 50% { opacity:0.72; } 100% { offset-distance:100%; opacity:0.72; } }
        @keyframes sp-rto2 { 0% { offset-distance:0%; opacity:0.52; } 50% { opacity:0.52; } 100% { offset-distance:100%; opacity:0.52; } }
        @keyframes sp-rto3 { 0% { offset-distance:0%; opacity:0.36; } 50% { opacity:0.36; } 100% { offset-distance:100%; opacity:0.36; } }
        @keyframes sp-rto4 { 0% { offset-distance:0%; opacity:0.24; } 50% { opacity:0.24; } 100% { offset-distance:100%; opacity:0.24; } }
        @keyframes sp-rto5 { 0% { offset-distance:0%; opacity:0.15; } 50% { opacity:0.15; } 100% { offset-distance:100%; opacity:0.15; } }
        @keyframes sp-rto6 { 0% { offset-distance:0%; opacity:0.08; } 50% { opacity:0.08; } 100% { offset-distance:100%; opacity:0.08; } }
        @keyframes sp-rto7 { 0% { offset-distance:0%; opacity:0.04; } 50% { opacity:0.04; } 100% { offset-distance:100%; opacity:0.04; } }
      `}</style>
    </div>
  );
}

function BillingPage(props: {
  intents: CryptoPayIntent[];
  refreshIntents: () => Promise<void> | void;
  refreshProfile: () => Promise<void> | void;
  onPaymentReceived: (intent: CryptoPayIntent) => void;
  subscriptionStatus: SubscriptionStatus;
  daysUntilExpiry: number | null;
  isLifetime: boolean;
  onOpenSubscribe: (initial?: PlanInterval | null) => void;
  onResubscribe: () => void;
}) {
  const auth = useMemo(() => getAuth(), []);
  const idToken = auth.idToken;
  const { intents, refreshIntents, refreshProfile, onPaymentReceived, subscriptionStatus, daysUntilExpiry, isLifetime, onOpenSubscribe, onResubscribe } = props;

  // Local profile fetch — we still want the full payload (creditsUsdCents,
  // planActiveUntil) on this page even though AuthedShell carries a lighter
  // copy. Refreshing on demand keeps the page in sync after a payment lands.
  const [profile, setProfile] = useState<any | null>(null);
  const [modalIntent, setModalIntent] = useState<any | null>(null);
  const [historyIntent, setHistoryIntent] = useState<CryptoPayIntent | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [addCreditsOpen, setAddCreditsOpen] = useState(false);
  const [planBusy, setPlanBusy] = useState<PlanInterval | null>(null);

  const loadProfile = useCallback(async () => {
    if (!idToken) return;
    try {
      const p = await getProfile(idToken);
      setProfile(p.profile);
    } catch { /* ignore */ }
  }, [idToken]);

  useEffect(() => {
    loadProfile().finally(() => setInitialLoading(false));
  }, [loadProfile]);

  // After a payment lands AuthedShell calls refreshProfile, but the local
  // BillingPage profile state lives independently — refresh it whenever the
  // global intents list changes so credits / plan stay current here too.
  useEffect(() => {
    loadProfile();
  }, [intents, loadProfile]);

  const handleSelectPlan = async (interval: PlanInterval) => {
    if (!idToken || planBusy) return;
    const priceCents = Math.round(PLAN_PRICE_USD[interval] * 100);
    const credits = Number(profile?.creditsUsdCents ?? 0);
    // Insufficient credits → kick over to SubscribeModal pre-targeted to the
    // chosen plan so the user can pay via crypto without leaving the flow.
    if (credits < priceCents) {
      onOpenSubscribe(interval);
      return;
    }
    setPlanBusy(interval);
    try {
      const r = await activatePlan({ idToken, interval });
      if (r.via === 'credits') {
        await Promise.all([loadProfile(), refreshProfile(), refreshIntents()]);
      }
    } catch {
      // TODO: surface error
    } finally {
      setPlanBusy(null);
    }
  };

  const liveModalIntent = useMemo<CryptoPayIntent | null>(() => {
    if (!modalIntent) return null;
    const fromList = intents.find(i => i.intentId === modalIntent.intentId);
    if (fromList) return { ...modalIntent, status: fromList.status, paidAt: fromList.paidAt, paidAmount: fromList.paidAmount };
    return modalIntent;
  }, [modalIntent, intents]);

  const nowSec = Math.floor(Date.now() / 1000);
  const planActive =
    !!profile?.planInterval &&
    profile.planInterval !== 'free' &&
    Number(profile.planActiveUntil ?? 0) > nowSec;

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <Card>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <CardTitle style={{ margin: 0 }}>Billing</CardTitle>
          {initialLoading && <Spinner size={14} />}
        </div>
        <Muted style={{ marginTop: 6 }}>Manage your plan, credits, and delivery access.</Muted>
        <Hr />

        {!initialLoading && subscriptionStatus !== 'active' && (
          <SubscriptionAlert
            status={subscriptionStatus}
            daysUntilExpiry={daysUntilExpiry}
            onResubscribe={onResubscribe}
            style={{ marginBottom: 14 }}
          />
        )}

        {!initialLoading && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <BillingStatBox
              label="Credits"
              value={`$${((profile?.creditsUsdCents ?? 0) / 100).toFixed(2)}`}
              actionLabel="Add more"
              onAction={() => setAddCreditsOpen(true)}
              actionDisabled={!idToken}
            />
            <BillingStatBox
              label="Plan"
              value={(() => {
                const p = profile?.planInterval ? String(profile.planInterval) : 'free';
                return p.charAt(0).toUpperCase() + p.slice(1);
              })()}
              subtitle={
                planActive && !isLifetime && Number(profile?.planActiveUntil ?? 0) > 0
                  ? (profile?.cancelledAt
                    ? `Cancelled — ends ${fmtDateOrdinal(Number(profile.planActiveUntil))}`
                    : `Active until ${fmtDateOrdinal(Number(profile.planActiveUntil))}`)
                  : null
              }
              actionLabel={planActive && !isLifetime ? 'Manage' : null}
              onAction={() => onOpenSubscribe()}
            />
          </div>
        )}
      </Card>

      {!initialLoading && !planActive && (
        <SubscriptionPlans
          currentInterval={null}
          onSelect={handleSelectPlan}
          busyInterval={planBusy}
        />
      )}

      <PaymentHistoryCard
        intents={intents}
        loading={initialLoading}
        onOpen={(it) => {
          if (it.status === 'EXPIRED' || it.status === 'FAILED' || it.status === 'CANCELED') return;
          if (it.status === 'PENDING' || it.status === 'CONFIRMING') {
            setModalIntent(it);
          } else {
            setHistoryIntent(it);
          }
        }}
      />

      {liveModalIntent && (
        <PaymentModal intent={liveModalIntent} onClose={() => setModalIntent(null)} />
      )}

      {historyIntent && (
        <PaymentDetailsModal intent={historyIntent} onClose={() => setHistoryIntent(null)} />
      )}

      {addCreditsOpen && idToken && (
        <AddCreditsModal
          idToken={idToken}
          onClose={() => { setAddCreditsOpen(false); refreshIntents(); }}
          onPaid={(paid) => { setAddCreditsOpen(false); onPaymentReceived(paid); }}
        />
      )}
    </div>
  );
}

function statusStyle(status: string): { bg: string; color: string; border: string } {
  if (status === 'PAID') return { bg: 'rgba(98,176,121,0.12)', color: '#8fd5a5', border: 'rgba(98,176,121,0.35)' };
  if (status === 'EXPIRED' || status === 'CANCELED') return { bg: 'rgba(180,180,180,0.08)', color: 'rgba(233,237,243,0.55)', border: 'rgba(180,180,180,0.25)' };
  return { bg: 'rgba(200,173,106,0.10)', color: '#c8ad6a', border: 'rgba(200,173,106,0.35)' };
}

function PaymentHistoryCard({ intents, loading, onOpen }: { intents: CryptoPayIntent[]; loading?: boolean; onOpen: (it: CryptoPayIntent) => void }) {
  return (
    <Card>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <CardTitle style={{ margin: 0 }}>Payment history</CardTitle>
        {loading && <Spinner size={14} />}
      </div>
      <Muted style={{ marginTop: 6 }}>Recent crypto payments. Pending ones auto-refresh.</Muted>
      <Hr />
      {intents.length === 0 ? (
        <Muted style={{ fontSize: 13 }}>Nothing here yet.</Muted>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {[...intents].sort((a, b) => (b.createdAt ?? 0) - (a.createdAt ?? 0)).map((it) => {
            const s = statusStyle(it.status);
            const inactive = it.status === 'EXPIRED' || it.status === 'CANCELED' || it.status === 'FAILED';
            return (
              <div
                key={it.intentId}
                onClick={() => { if (!inactive) onOpen(it); }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 12,
                  border: `1px solid ${theme.colors.line}`,
                  background: 'rgba(0,0,0,0.12)',
                  cursor: inactive ? 'default' : 'pointer',
                  opacity: inactive ? 0.55 : 1,
                }}
              >
                <div style={{ display: 'grid', gap: 2 }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>${(it.amountUsdCents / 100).toFixed(2)} USDC</div>
                  <div style={{ fontSize: 11, color: theme.colors.muted }}>
                    {fmtRelative(it.createdAt)}
                  </div>
                </div>
                <span style={{
                  fontSize: 11,
                  fontWeight: 800,
                  letterSpacing: 0.4,
                  padding: '4px 10px',
                  borderRadius: 999,
                  background: s.bg,
                  color: s.color,
                  border: `1px solid ${s.border}`,
                }}>{it.status}</span>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function PaymentDetailsModal({ intent, onClose }: { intent: CryptoPayIntent; onClose: () => void }) {
  const status = String(intent?.status ?? '');
  const s = statusStyle(status);
  const isPending = status === 'PENDING' || status === 'CONFIRMING';
  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: 460 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
          <CardTitle style={{ margin: 0 }}>Payment details</CardTitle>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            {isPending && <Spinner size={14} />}
            <span style={{
              fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
              padding: '4px 10px', borderRadius: 999,
              background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            }}>{status}</span>
          </div>
        </div>
        {isPending ? (
          <Muted style={{ marginTop: 6, fontSize: 12 }}>Waiting for payment confirmation…</Muted>
        ) : null}
        <Hr />

        <div style={{ display: 'grid', gap: 8 }}>
          <DetailRow label="Amount" value={`$${(intent.amountUsdCents / 100).toFixed(2)} USD`} />
          <DetailRow label="Pay address" value={String(intent.payAddress)} mono />
          <DetailRow label="Pay amount" value={`${intent.payAmount} ${(intent.payCurrency ?? '').toUpperCase()}`} />
          <DetailRow label="Created" value={fmtRelative(intent.createdAt)} />
          {intent.paidAt ? <DetailRow label="Paid" value={fmtRelative(intent.paidAt)} /> : null}
          {intent.paidAmount ? <DetailRow label="Paid amount" value={`${intent.paidAmount} ${(intent.payCurrency ?? '').toUpperCase()}`} /> : null}
        </div>

        <Hr />
        <ButtonRow>
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Close</Button>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

function DetailRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div style={{
      padding: '8px 10px',
      borderRadius: 10,
      border: `1px solid ${theme.colors.line}`,
      background: 'rgba(0,0,0,0.12)',
    }}>
      <div style={{ fontSize: 11, color: theme.colors.muted }}>{label}</div>
      <div style={{
        fontSize: 13, marginTop: 2,
        fontFamily: mono ? 'ui-monospace, Menlo, monospace' : undefined,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{value}</div>
    </div>
  );
}

// Currencies we surface in the AddCreditsModal dropdown. Stays in sync with
// the SUPPORTED_PAY_CURRENCIES allowlist on the backend. `unit` is the short
// ticker shown next to amounts; `chainLabel` qualifies the chain when relevant.
// `displayDecimals` is the user-facing precision — must match (or be lower
// than) the on-chain precision of the asset, otherwise wallets like Solflare
// silently truncate and the customer underpays into `partially_paid`.
const PAY_CURRENCIES: Array<{ code: string; label: string; unit: string; chainLabel?: string; hint?: string; displayDecimals: number }> = [
  { code: 'usdcsol',   label: 'USDC (Solana)',  unit: 'USDC', chainLabel: 'on Solana',  displayDecimals: 6 },
  { code: 'usdttrc20', label: 'USDT (Tron)',    unit: 'USDT', chainLabel: 'on Tron',    displayDecimals: 6, hint: 'Cheapest fees' },
  { code: 'usdcmatic', label: 'USDC (Polygon)', unit: 'USDC', chainLabel: 'on Polygon', displayDecimals: 6 },
];

function prettyCurrency(code: string | undefined): { unit: string; chainLabel?: string; displayDecimals: number } {
  const c = PAY_CURRENCIES.find(p => p.code === (code ?? '').toLowerCase());
  return c
    ? { unit: c.unit, chainLabel: c.chainLabel, displayDecimals: c.displayDecimals }
    : { unit: (code ?? '').toUpperCase(), displayDecimals: 8 };
}

// Round an amount string UP to `decimals` places of precision using BigInt so
// we don't introduce float drift. Lets us quote a number the user's wallet
// can actually represent on-chain — they overpay by ≤1 unit at that
// precision, NowPayments treats it as "finished", and we credit normally.
function roundUpToPrecision(amount: string, decimals: number): string {
  const m = String(amount).trim().match(/^(\d+)(?:\.(\d*))?$/);
  if (!m) return String(amount);
  const whole = m[1];
  const frac = m[2] ?? '';
  if (frac.length <= decimals) {
    return decimals > 0 ? `${whole}.${frac.padEnd(decimals, '0')}` : whole;
  }
  const kept = frac.slice(0, decimals);
  const rest = frac.slice(decimals);
  const hasRest = !/^0*$/.test(rest);
  let scaled = BigInt(whole + kept);
  if (hasRest) scaled += 1n;
  if (decimals === 0) return scaled.toString();
  const s = scaled.toString().padStart(decimals + 1, '0');
  return `${s.slice(0, s.length - decimals)}.${s.slice(s.length - decimals)}`;
}

// "10079m 46s" is unreadable. Break long deltas into days/hours/mins so a
// 7-day expiration shows as "7d 0h" instead of a five-digit minute counter.
function formatDuration(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  if (s >= 86400) {
    const d = Math.floor(s / 86400);
    const h = Math.floor((s % 86400) / 3600);
    return `${d}d ${h}h`;
  }
  if (s >= 3600) {
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    return `${h}h ${m}m`;
  }
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${String(sec).padStart(2, '0')}s`;
}

// Themed dropdown — the native <select> doesn't match the rest of the UI.
// The option panel is portaled to document.body and positioned with fixed
// coordinates so it can overlay outside the modal without forcing the modal
// to grow / become scrollable.
function ThemedSelect<T extends string>({
  value, onChange, options, disabled,
}: {
  value: T;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string; hint?: string }>;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const current = options.find(o => o.value === value);

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return;
    setRect(triggerRef.current.getBoundingClientRect());
  }, [open]);

  // Reposition on scroll/resize while open so the panel tracks the trigger.
  useEffect(() => {
    if (!open) return;
    const update = () => {
      if (triggerRef.current) setRect(triggerRef.current.getBoundingClientRect());
    };
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          width: '100%',
          textAlign: 'left',
          background: 'rgba(0,0,0,0.20)',
          color: theme.colors.text,
          border: `1px solid ${open ? 'rgba(200,173,106,0.45)' : theme.colors.line}`,
          borderRadius: 10,
          padding: '10px 36px 10px 12px',
          fontSize: 14,
          fontFamily: 'inherit',
          cursor: disabled ? 'default' : 'pointer',
          opacity: disabled ? 0.6 : 1,
          position: 'relative',
          transition: 'border-color 120ms ease',
        }}
      >
        {current ? (
          <>
            <span>{current.label}</span>
            {current.hint ? (
              <span style={{ color: theme.colors.muted, marginLeft: 8, fontSize: 12 }}>· {current.hint}</span>
            ) : null}
          </>
        ) : (
          <span style={{ color: theme.colors.muted }}>Select…</span>
        )}
        <span style={{
          position: 'absolute', right: 12, top: '50%', transform: `translateY(-50%) rotate(${open ? 180 : 0}deg)`,
          transition: 'transform 160ms ease', color: theme.colors.muted, pointerEvents: 'none',
          fontSize: 10,
        }}>▾</span>
      </button>

      {open && rect && createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            top: rect.bottom + 4,
            left: rect.left,
            width: rect.width,
            background: '#0e1116',
            border: `1px solid ${theme.colors.line}`,
            borderRadius: 10,
            boxShadow: '0 18px 40px rgba(0,0,0,0.55)',
            zIndex: 1000,
            padding: 4,
            maxHeight: 280,
            overflowY: 'auto',
          }}
          role="listbox"
        >
          {options.map(o => {
            const selected = o.value === value;
            return (
              <button
                key={o.value}
                type="button"
                role="option"
                aria-selected={selected}
                onClick={() => { onChange(o.value); setOpen(false); }}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  background: selected ? 'rgba(200,173,106,0.12)' : 'transparent',
                  color: theme.colors.text,
                  border: 'none',
                  borderRadius: 8,
                  padding: '9px 10px',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                }}
                onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.04)'; }}
                onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <span>{o.label}</span>
                {o.hint ? (
                  <span style={{ color: theme.colors.muted, fontSize: 12 }}>{o.hint}</span>
                ) : null}
              </button>
            );
          })}
        </div>,
        document.body,
      )}
    </>
  );
}

async function clipboardCopy(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch { /* fallthrough */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.top = '0';
    ta.style.left = '0';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// Shared deposit-info layout: status badge, instructions, expiration timer,
// QR, copy rows. Reused by PaymentModal (reopen pending intent from history)
// and AddCreditsModal step 2 (just-created intent).
function PayInfoBody({ intent }: { intent: CryptoPayIntent }) {
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [now, setNow] = useState(() => Math.floor(Date.now() / 1000));

  useEffect(() => {
    const t = window.setInterval(() => setNow(Math.floor(Date.now() / 1000)), 1000);
    return () => window.clearInterval(t);
  }, []);

  // QR encodes just the pay address — most wallet camera scanners accept it.
  // Chain-specific URI schemes (bitcoin:, ethereum:, solana:) would also work
  // but aren't universally supported across wallets/exchanges.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!intent?.payAddress) return;
      const QR = await import('qrcode');
      const url = await QR.toDataURL(intent.payAddress, { width: 240, margin: 1, color: { dark: '#0b0c0f', light: '#ffffff' } });
      if (!cancelled) setQrDataUrl(url);
    })();
    return () => { cancelled = true; };
  }, [intent?.payAddress]);

  const status = String(intent?.status ?? 'PENDING');
  const s = statusStyle(status);
  const expiresIn = typeof intent?.expiresAt === 'number' ? Math.max(0, intent.expiresAt - now) : null;
  const { unit, chainLabel, displayDecimals } = prettyCurrency(intent.payCurrency);
  // NowPayments quotes more decimals than most chains can settle (e.g. 8
  // decimals for USDC, which only has 6). Wallets truncate on the user's
  // side, leaving a "partially_paid" remainder. Round up here so the user
  // sends a wallet-representable amount that's ≥ the quoted amount.
  const displayPayAmount = roundUpToPrecision(String(intent.payAmount), displayDecimals);

  const copy = async (text: string, label: string) => {
    if (await clipboardCopy(text)) {
      setCopied(label);
      window.setTimeout(() => setCopied(null), 1200);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
        <CardTitle style={{ margin: 0 }}>Add ${(intent.amountUsdCents / 100).toFixed(2)} in credits</CardTitle>
        <span style={{
          fontSize: 11, fontWeight: 800, letterSpacing: 0.4,
          padding: '4px 10px', borderRadius: 999,
          background: s.bg, color: s.color, border: `1px solid ${s.border}`,
        }}>{status}</span>
      </div>
      <Muted style={{ marginTop: 10 }}>
        Send <b style={{ color: theme.colors.text }}>{displayPayAmount} {unit}</b>{chainLabel ? <> {chainLabel}</> : null} to the address below from any wallet or exchange. We'll credit your account once the network confirms the transfer.
      </Muted>
      {expiresIn !== null ? (
        <div style={{ marginTop: 6, fontSize: 13, color: theme.colors.muted }}>
          {expiresIn <= 0 ? (
            <b style={{ color: theme.colors.danger }}>Expired</b>
          ) : (
            <>Expires in <b style={{ color: theme.colors.text }}>{formatDuration(expiresIn)}</b></>
          )}
        </div>
      ) : null}
      <Hr />

      {qrDataUrl ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4px 0 12px' }}>
          <img src={qrDataUrl} alt="Pay address QR" style={{ width: 240, height: 240, borderRadius: 12, border: `1px solid ${theme.colors.line}` }} />
        </div>
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
          <Spinner size={24} />
        </div>
      )}

      <div style={{ display: 'grid', gap: 8 }}>
        <CopyRow
          label={`Amount (${unit})`}
          value={displayPayAmount}
          copy={() => copy(displayPayAmount, 'amount')}
          copied={copied === 'amount'}
        />
        <CopyRow
          label={chainLabel ? `Pay address (${chainLabel.replace(/^on /, '')})` : 'Pay address'}
          value={String(intent.payAddress)}
          copy={() => copy(String(intent.payAddress), 'addr')}
          copied={copied === 'addr'}
          truncate
        />
      </div>

      <Muted style={{ marginTop: 12, fontSize: 12 }}>
        Send the exact amount shown — under-sending leaves the payment in a partial state and may require manual reconciliation.
      </Muted>
    </>
  );
}

function PaymentModal({ intent, onClose }: { intent: CryptoPayIntent; onClose: () => void }) {
  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: 460 }}>
        <PayInfoBody intent={intent} />
        <Hr />
        <ButtonRow>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Done</Button>
        </ButtonRow>
      </Modal>
    </Overlay>
  );
}

function BillingStatBox({
  label, value, subtitle,
  actionLabel, onAction, actionDisabled,
}: {
  label: string;
  value: any;
  subtitle?: string | null;
  actionLabel: string | null;
  onAction?: () => void;
  actionDisabled?: boolean;
}) {
  const v = value == null ? '—' : String(value);
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${theme.colors.line}`,
        background: 'rgba(0,0,0,0.10)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: theme.colors.muted }}>{label}</div>
        <div style={{ fontSize: 17, marginTop: 6, fontWeight: 600, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{v}</div>
        {subtitle && (
          <div style={{ fontSize: 12, marginTop: 4, color: theme.colors.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subtitle}</div>
        )}
      </div>
      {actionLabel && (
        <Button variant="ghost" onClick={onAction} disabled={actionDisabled}>
          {actionLabel}
        </Button>
      )}
    </div>
  );
}

function AddCreditsModal({ idToken, onClose, onPaid }: { idToken: string; onClose: () => void; onPaid?: (intent: CryptoPayIntent) => void }) {
  const [step, setStep] = useState<'form' | 'pay'>('form');
  const [amountUsd, setAmountUsd] = useState('10');
  const [currency, setCurrency] = useState(PAY_CURRENCIES[0].code);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [intent, setIntent] = useState<CryptoPayIntent | null>(null);
  const [minUsd, setMinUsd] = useState<number | null>(null);
  const [minLoading, setMinLoading] = useState(false);

  // Refetch the NowPayments minimum each time the user picks a currency.
  // Prices move, so a cached BTC min from last week could under-call the
  // current one. Cheap RPC, fine to do per change.
  useEffect(() => {
    let cancelled = false;
    setMinUsd(null);
    setMinLoading(true);
    getCryptoMinAmount({ idToken, currency })
      .then(r => { if (!cancelled) setMinUsd(r.minUsd ?? null); })
      .catch(() => { if (!cancelled) setMinUsd(null); })
      .finally(() => { if (!cancelled) setMinLoading(false); });
    return () => { cancelled = true; };
  }, [idToken, currency]);

  const amountNum = Number(amountUsd);
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const belowMin = amountValid && minUsd != null && amountNum < minUsd;
  const canSubmit = amountValid && !belowMin && !minLoading;

  // Poll the just-created intent so the status badge inside PayInfoBody reflects
  // PENDING → CONFIRMING → PAID without depending on the parent's list poller
  // (the modal can outlive a parent re-render or the user can close before the
  // parent gets a chance to refetch).
  useEffect(() => {
    if (step !== 'pay' || !intent) return;
    if (intent.status === 'PAID' || intent.status === 'EXPIRED' || intent.status === 'FAILED') return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await getCryptoPayIntent({ idToken, intentId: intent.intentId });
        if (alive && r.intent) setIntent(r.intent);
      } catch { /* transient */ }
    };
    const t = window.setInterval(tick, 3000);
    return () => { alive = false; window.clearInterval(t); };
  }, [step, intent, idToken]);

  // When the active intent flips to PAID, hand it back up to the page so it
  // can swap our deposit modal out for the green-check confirmation overlay.
  // Without this the parent's list-poll would also notice the transition, but
  // its closure-snapshot of `intents` doesn't include modal-created intents,
  // so the green check would never fire while the deposit modal is open.
  useEffect(() => {
    if (intent?.status === 'PAID' && onPaid) onPaid(intent);
  }, [intent?.status]);

  const onGenerate = async () => {
    if (busy) return;
    if (!amountValid) { setErr('Enter an amount greater than 0.'); return; }
    if (belowMin && minUsd != null) { setErr(`Minimum for this currency is $${minUsd.toFixed(2)}.`); return; }
    setBusy(true);
    setErr(null);
    try {
      const r = await createCryptoPayIntent({ idToken, amountUsdCents: Math.round(amountNum * 100), payCurrency: currency });
      setIntent(r.intent);
      setStep('pay');
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: 460 }}>
        {step === 'form' ? (
          busy ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '40px 0' }}>
              <Spinner size={32} />
              <Muted style={{ fontSize: 13 }}>Generating payment…</Muted>
            </div>
          ) : (
            <>
              <CardTitle style={{ margin: 0 }}>Add credits</CardTitle>
              <Muted style={{ marginTop: 6 }}>Pick how much to add and the currency to pay with.</Muted>
              <Hr />

              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <Label>Amount (USD)</Label>
                  <Input
                    value={amountUsd}
                    onChange={(e) => { setAmountUsd(e.target.value); if (err) setErr(null); }}
                    placeholder="10"
                  />
                  {belowMin && minUsd != null ? (
                    <div style={{ fontSize: 12, color: theme.colors.danger }}>
                      Minimum for this currency is ${minUsd.toFixed(2)}.
                    </div>
                  ) : null}
                </div>

                <div style={{ display: 'grid', gap: 6 }}>
                  <Label>Pay with</Label>
                  <ThemedSelect
                    value={currency}
                    onChange={setCurrency}
                    options={PAY_CURRENCIES.map(c => ({ value: c.code, label: c.label, hint: c.hint }))}
                  />
                  <div style={{ fontSize: 12, color: theme.colors.muted, minHeight: 16 }}>
                    {minLoading
                      ? 'Loading minimum…'
                      : minUsd != null
                        ? `Minimum: $${minUsd.toFixed(2)}`
                        : ''}
                  </div>
                </div>
              </div>

              {err ? <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{err}</div> : null}

              <Hr />
              <ButtonRow>
                <Button variant="ghost" onClick={onClose}>Cancel</Button>
                <div style={{ flex: 1 }} />
                <Button onClick={onGenerate} disabled={!canSubmit}>Generate payment</Button>
              </ButtonRow>
            </>
          )
        ) : (
          intent && (
            <>
              <PayInfoBody intent={intent} />
              <Hr />
              <ButtonRow>
                <div style={{ flex: 1 }} />
                <Button onClick={onClose}>Done</Button>
              </ButtonRow>
            </>
          )
        )}
      </Modal>
    </Overlay>
  );
}

const PLAN_PRICE_USD: Record<PlanInterval, number> = { monthly: 7, yearly: 60, lifetime: 300 };

function SubscribeModal({
  idToken,
  creditsUsdCents,
  initialPlan,
  currentSubInterval,
  alreadyCancelled,
  onClose,
  onPaid,
  onCancelled,
}: {
  idToken: string;
  creditsUsdCents: number;
  initialPlan?: PlanInterval | null;
  currentSubInterval?: PlanInterval | null;
  alreadyCancelled?: boolean;
  onClose: () => void;
  onPaid?: (intent: CryptoPayIntent) => void;
  onCancelled?: () => void;
}) {
  // When the caller pre-selects a plan (Resubscribe / Change / insufficient-
  // credits fallback from the billing-page plan cards), skip straight past the
  // picker into the right second step based on the user's balance.
  const initialStep: 'plan' | 'credits' | 'currency' = initialPlan
    ? (creditsUsdCents >= Math.round(PLAN_PRICE_USD[initialPlan] * 100) ? 'credits' : 'currency')
    : 'plan';
  const [step, setStep] = useState<'plan' | 'credits' | 'currency' | 'pay' | 'cancel'>(initialStep);
  const [chosenPlan, setChosenPlan] = useState<PlanInterval | null>(initialPlan ?? null);
  const showCancelLink = !!currentSubInterval && !alreadyCancelled;
  const [currency, setCurrency] = useState(PAY_CURRENCIES[0].code);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [intent, setIntent] = useState<CryptoPayIntent | null>(null);
  const [minUsd, setMinUsd] = useState<number | null>(null);
  const [minLoading, setMinLoading] = useState(false);

  const planPriceUsd = chosenPlan ? PLAN_PRICE_USD[chosenPlan] : 0;
  const planLabel = chosenPlan ? chosenPlan.charAt(0).toUpperCase() + chosenPlan.slice(1) : '';

  // Pull current minimum for the picked currency. Same per-currency price
  // floor logic as AddCreditsModal — needed because plan prices ($7 monthly)
  // can land below the BTC/ETH minimum, in which case we surface that and
  // make the user pick another currency.
  useEffect(() => {
    if (step !== 'currency') return;
    let cancelled = false;
    setMinUsd(null);
    setMinLoading(true);
    getCryptoMinAmount({ idToken, currency })
      .then((r) => { if (!cancelled) setMinUsd(r.minUsd ?? null); })
      .catch(() => { if (!cancelled) setMinUsd(null); })
      .finally(() => { if (!cancelled) setMinLoading(false); });
    return () => { cancelled = true; };
  }, [step, idToken, currency]);

  const belowMin = minUsd != null && planPriceUsd < minUsd;

  // Poll the active intent so the pay screen shows live status.
  useEffect(() => {
    if (step !== 'pay' || !intent) return;
    if (intent.status === 'PAID' || intent.status === 'EXPIRED' || intent.status === 'FAILED') return;
    let alive = true;
    const tick = async () => {
      try {
        const r = await getCryptoPayIntent({ idToken, intentId: intent.intentId });
        if (alive && r.intent) setIntent(r.intent);
      } catch { /* transient */ }
    };
    const t = window.setInterval(tick, 3000);
    return () => { alive = false; window.clearInterval(t); };
  }, [step, intent, idToken]);

  // Hand the PAID intent up so the parent can swap our modal for the
  // celebratory confirmation overlay.
  useEffect(() => {
    if (intent?.status === 'PAID' && onPaid) onPaid(intent);
  }, [intent?.status]);

  const onPickPlan = (p: PlanInterval) => {
    setChosenPlan(p);
    setErr(null);
    const priceCents = Math.round(PLAN_PRICE_USD[p] * 100);
    // Credits-first when the wallet already covers the chosen plan — saves
    // the user a redundant on-chain payment.
    setStep(creditsUsdCents >= priceCents ? 'credits' : 'currency');
  };

  const onActivateFromCredits = async () => {
    if (busy || !chosenPlan) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await activatePlan({ idToken, interval: chosenPlan });
      if (r.via === 'credits' && onPaid) {
        // No real intent for the credits-funded path; synthesize one so the
        // celebration modal renders the same plan-aware copy.
        const nowSec = Math.floor(Date.now() / 1000);
        const synthetic: CryptoPayIntent = {
          intentId: `local-credit-${nowSec}-${chosenPlan}`,
          status: 'PAID',
          createdAt: nowSec,
          expiresAt: nowSec,
          amountUsdCents: Math.round(planPriceUsd * 100),
          targetPlan: chosenPlan,
          payAddress: '',
          payAmount: '0',
          payCurrency: 'credits',
        };
        onPaid(synthetic);
      } else {
        setErr('Could not activate from credits. Try paying with crypto.');
        setStep('currency');
      }
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onGenerate = async () => {
    if (busy || !chosenPlan) return;
    if (belowMin && minUsd != null) {
      setErr(`$${planPriceUsd.toFixed(2)} is below the ${currency.toUpperCase()} minimum. Pick a different currency.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const r = await createCryptoPayIntent({
        idToken,
        amountUsdCents: Math.round(planPriceUsd * 100),
        payCurrency: currency,
        targetPlan: chosenPlan,
      });
      setIntent(r.intent);
      setStep('pay');
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const onConfirmCancel = async () => {
    if (busy) return;
    setBusy(true);
    setErr(null);
    try {
      await cancelSubscription({ idToken });
      onCancelled?.();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Overlay onClick={(e: any) => { if (e.target === e.currentTarget) onClose(); }}>
      <Modal style={{ maxWidth: step === 'plan' ? 880 : 480 }}>
        {step === 'plan' && (
          <>
            <CardTitle style={{ margin: 0 }}>{currentSubInterval ? 'Manage subscription' : 'Subscribe'}</CardTitle>
            <Muted style={{ marginTop: 6 }}>
              {currentSubInterval ? 'Switch your plan or cancel below.' : 'Pick a plan to activate automatic delivery.'}
            </Muted>
            <Hr />
            <SubscriptionPlans currentInterval={currentSubInterval ?? null} onSelect={onPickPlan} />
            <Hr />
            <ButtonRow>
              <Button variant="ghost" onClick={onClose}>Close</Button>
              <div style={{ flex: 1 }} />
              {showCancelLink && (
                <a
                  href="#"
                  onClick={(e) => { e.preventDefault(); setErr(null); setStep('cancel'); }}
                  style={{ color: theme.colors.muted, fontSize: 13, textDecoration: 'underline', textUnderlineOffset: 2 }}
                >
                  Cancel subscription
                </a>
              )}
            </ButtonRow>
          </>
        )}

        {step === 'cancel' && currentSubInterval && (
          busy ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '40px 0' }}>
              <Spinner size={32} />
              <Muted style={{ fontSize: 13 }}>Cancelling…</Muted>
            </div>
          ) : (
            <>
              <CardTitle style={{ margin: 0 }}>We're sorry to see you go.</CardTitle>
              <Hr />

              <div style={{
                padding: '12px 16px',
                borderRadius: 10,
                border: '1px solid rgba(245,245,247,0.10)',
                background: 'rgba(0,0,0,0.18)',
                fontSize: 13,
                lineHeight: 1.55,
                color: theme.colors.text,
                display: 'grid',
                gap: 8,
              }}>
                <div>Your plan stays active for the rest of the period you've already paid for.</div>
                <div>After that, automatic delivery stops.</div>
                <div style={{ opacity: 0.72 }}>Your vault, messages, and any remaining credits are kept on your account. You can resubscribe anytime.</div>
                <div style={{ opacity: 0.72 }}>No refunds are issued for the unused portion.</div>
              </div>

              {err ? <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{err}</div> : null}

              <Hr />
              <ButtonRow>
                <Button variant="ghost" onClick={() => setStep('plan')}>Back</Button>
                <div style={{ flex: 1 }} />
                <Button variant="danger" onClick={onConfirmCancel}>Cancel subscription</Button>
              </ButtonRow>
            </>
          )
        )}

        {step === 'credits' && chosenPlan && (
          busy ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '40px 0' }}>
              <Spinner size={32} />
              <Muted style={{ fontSize: 13 }}>Activating…</Muted>
            </div>
          ) : (
            <>
              <CardTitle style={{ margin: 0 }}>Confirm subscription</CardTitle>
              <Muted style={{ marginTop: 6 }}>
                {planLabel} plan — ${planPriceUsd.toFixed(2)}{chosenPlan === 'lifetime' ? ' one-time' : ''}
              </Muted>
              <Hr />

              <div style={{ fontSize: 13, lineHeight: 1.5, color: theme.colors.text }}>
                You have <strong>${(creditsUsdCents / 100).toFixed(2)}</strong> in credits. Activating {planLabel} will spend ${planPriceUsd.toFixed(2)} and leave ${((creditsUsdCents - Math.round(planPriceUsd * 100)) / 100).toFixed(2)} on your balance.
              </div>

              {err ? <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{err}</div> : null}

              <Hr />
              <ButtonRow>
                <Button variant="ghost" onClick={() => setStep('plan')}>Back</Button>
                <div style={{ flex: 1 }} />
                <Button onClick={onActivateFromCredits}>Activate with credits</Button>
              </ButtonRow>
            </>
          )
        )}

        {step === 'currency' && chosenPlan && (
          busy ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12, padding: '40px 0' }}>
              <Spinner size={32} />
              <Muted style={{ fontSize: 13 }}>Generating payment…</Muted>
            </div>
          ) : (
            <>
              <CardTitle style={{ margin: 0 }}>Pay with crypto</CardTitle>
              <Muted style={{ marginTop: 6 }}>
                {planLabel} plan — ${planPriceUsd.toFixed(2)}{chosenPlan === 'lifetime' ? ' one-time' : ''}
              </Muted>
              <Hr />

              <div style={{ display: 'grid', gap: 12 }}>
                <div style={{ display: 'grid', gap: 6 }}>
                  <Label>Pay with</Label>
                  <ThemedSelect
                    value={currency}
                    onChange={setCurrency}
                    options={PAY_CURRENCIES.map((c) => ({ value: c.code, label: c.label, hint: c.hint }))}
                  />
                  {/* Plan prices always clear the network minimum, so we don't
                      advertise it — but keep the guard below as a safety net in
                      case some currency's floor ever exceeds the plan price. */}
                  {belowMin && minUsd != null && (
                    <div style={{ fontSize: 12, color: theme.colors.danger }}>
                      ${planPriceUsd.toFixed(2)} is below the {currency.toUpperCase()} minimum (${minUsd.toFixed(2)}). Pick a different currency.
                    </div>
                  )}
                </div>
              </div>

              {err ? <div style={{ marginTop: 10, color: theme.colors.danger, fontSize: 13 }}>{err}</div> : null}

              <Hr />
              <ButtonRow>
                <Button variant="ghost" onClick={() => setStep('plan')}>Back</Button>
                <div style={{ flex: 1 }} />
                <Button onClick={onGenerate} disabled={belowMin || minLoading}>Generate payment</Button>
              </ButtonRow>
            </>
          )
        )}

        {step === 'pay' && intent && (
          <>
            <PayInfoBody intent={intent} />
            <Hr />
            <ButtonRow>
              <div style={{ flex: 1 }} />
              <Button onClick={onClose}>Done</Button>
            </ButtonRow>
          </>
        )}
      </Modal>
    </Overlay>
  );
}

function CopyRow({ label, value, copy, copied, truncate }: { label: string; value: string; copy: () => void; copied: boolean; truncate?: boolean }) {
  return (
    <div
      onClick={copy}
      style={{
        display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
        borderRadius: 10, border: `1px solid ${theme.colors.line}`, background: 'rgba(0,0,0,0.12)',
        cursor: 'pointer',
      }}
      title="Click to copy"
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: theme.colors.muted }}>{label}</div>
        <div style={{
          fontSize: 13, marginTop: 2,
          fontFamily: truncate ? 'ui-monospace, Menlo, monospace' : undefined,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{value}</div>
      </div>
      <span style={{
        position: 'relative',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 24, height: 24, flex: '0 0 24px',
        color: copied ? '#c8ad6a' : theme.colors.muted,
        transition: 'color 160ms ease',
      }}>
        <span style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: `translateX(-50%) translateY(${copied ? '0' : '4px'})`,
          padding: '3px 8px',
          borderRadius: 6,
          background: 'rgba(11,13,16,0.92)',
          border: '1px solid rgba(200,173,106,0.35)',
          color: '#c8ad6a',
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.2,
          whiteSpace: 'nowrap',
          opacity: copied ? 1 : 0,
          pointerEvents: 'none',
          transition: 'opacity 160ms ease, transform 160ms ease',
        }}>Copied</span>
        {copied ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <path d="M5 12.5 L10 17.5 L19 8" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
            <rect x="9" y="9" width="11" height="11" rx="2" />
            <path d="M5 15V6a2 2 0 0 1 2-2h9" />
          </svg>
        )}
      </span>
    </div>
  );
}

function Embers({ count = 16 }: { count?: number }) {
  // Top-left corner glow + embers drifting outward.
  //
  // Two-animation trick: motion is linear so embers move at constant speed
  // (no rush-then-hover artifact), opacity is ease-out so they fade in
  // quickly and trail off slowly without ever flickering. Each ember has
  // a negative animation-delay evenly distributed across the cycle, so at
  // any given moment a roughly-constant number are visible — no waves of
  // "everything spawned at once" then dead air.
  const DURATION = 22;

  return (
    <>
      <style>{`
        @keyframes sp-ember-move {
          0%   { transform: translate3d(0, 0, 0) scale(0.7); }
          100% { transform: translate3d(var(--sp-ember-dx, 60vw), var(--sp-ember-dy, 60vh), 0) scale(1); }
        }
        @keyframes sp-ember-glow {
          0%   { opacity: 0; }
          12%  { opacity: var(--sp-ember-peak, 0.4); }
          70%  { opacity: calc(var(--sp-ember-peak, 0.4) * 0.5); }
          100% { opacity: 0; }
        }
        /* Glow boots up from black over ~5s so the page doesn't snap-on
           when the loading animation finishes. After the spawn finishes,
           the gentle pulse takes over (last animation listed wins on the
           same property). */
        @keyframes sp-corner-glow-spawn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes sp-corner-glow-pulse {
          0%, 100% { opacity: 0.88; }
          50%      { opacity: 1; }
        }
      `}</style>
      <div aria-hidden style={{
        position: 'fixed',
        inset: 0,
        pointerEvents: 'none',
        zIndex: 0,
        overflow: 'hidden',
      }}>
        {/* Corner glow — radial wash anchored top-left. Spawns over 5s,
            then drifts into a 6s pulse. */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '75vw',
          height: '75vh',
          background: 'radial-gradient(ellipse 640px 540px at top left, rgba(200,173,106,0.16) 0%, rgba(200,173,106,0.07) 28%, rgba(200,173,106,0) 60%)',
          animation: 'sp-corner-glow-spawn 5s ease-out forwards, sp-corner-glow-pulse 6s ease-in-out 5s infinite',
        }} />

        {Array.from({ length: count }).map((_, i) => {
          // Spawn within ~22vw × 22vh of top-left so embers look like
          // they're emanating from the glow rather than scattered.
          const startX = ((i * 7) % 22);
          const startY = ((i * 5 + 3) % 22);
          // Drift outward (right + down). Per-ember angle variation so they
          // fan out rather than march in lockstep.
          const dx = 55 + ((i % 5) * 7);
          const dy = 50 + ((i % 4) * 8);
          // Brighter peak so embers actually read against the dark canvas
          // without losing the candle-lit feel.
          const peak = 0.32 + ((i % 4) * 0.06);
          const size = 3 + (i % 3);
          const delay = -((i / count) * DURATION);
          return (
            <div
              key={i}
              style={{
                position: 'absolute',
                top: `${startY}vh`,
                left: `${startX}vw`,
                width: size,
                height: size,
                borderRadius: '50%',
                background: '#d2b878',
                boxShadow: '0 0 6px rgba(200,173,106,0.85), 0 0 16px rgba(200,173,106,0.45), 0 0 32px rgba(200,173,106,0.18)',
                animation: `sp-ember-move ${DURATION}s linear ${delay}s infinite, sp-ember-glow ${DURATION}s ease-out ${delay}s infinite`,
                ['--sp-ember-dx' as any]: `${dx}vw`,
                ['--sp-ember-dy' as any]: `${dy}vh`,
                ['--sp-ember-peak' as any]: `${peak}`,
              } as React.CSSProperties}
            />
          );
        })}
      </div>
    </>
  );
}

function RedeemPage() {
  const [token, setToken] = useState(() => new URLSearchParams(window.location.search).get('token') ?? '');
  const [result, setResult] = useState<any>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const didAutoRedeem = useRef(false);
  const hasMessage = !!result?.plaintext;

  // Three-stage open animation:
  //   idle    — token form, no animation
  //   loading — sealed envelope + comet looping while the API responds
  //   opening — flap unseals and particles fly outward; held for 1s before
  //   shown   — animation hidden, message + attachments rendered
  // Initialize to 'loading' synchronously when the URL carries a token, so the
  // token-form doesn't flash for one paint while the auto-redeem effect runs.
  const [animPhase, setAnimPhase] = useState<'idle' | 'loading' | 'opening' | 'shown'>(
    () => (new URLSearchParams(window.location.search).get('token') ? 'loading' : 'idle')
  );

  useEffect(() => {
    if (busy && !hasMessage) {
      setAnimPhase('loading');
      return;
    }
    if (hasMessage && (animPhase === 'loading' || animPhase === 'idle')) {
      setAnimPhase('opening');
      // Opening sequence: 700ms seal-out + 600ms flap rotation = 1300ms anim,
      // then 1500ms held-open beat before content fades in.
      const t = window.setTimeout(() => setAnimPhase('shown'), 2800);
      return () => window.clearTimeout(t);
    }
    if (err && (animPhase === 'loading' || animPhase === 'opening')) {
      setAnimPhase('idle');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [busy, hasMessage, err]);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const t = qs.get('token');
    if (t) {
      setToken(t);
      // React StrictMode runs effects twice in dev; guard so we don't burn one-time tokens.
      if (!didAutoRedeem.current) {
        didAutoRedeem.current = true;
        Promise.resolve().then(() => onRedeem(t));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRedeem = async (overrideToken?: string) => {
    const t = (overrideToken ?? token).trim();
    if (!t || busy) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await redeemToken(t);
      setResult(r);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
      setResult(null);
    } finally {
      setBusy(false);
    }
  };

  const [downloaded, setDownloaded] = useState(false);

  const onDownloadMessage = () => {
    const text = String(result?.plaintext ?? '');
    if (!text) return;
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'message.txt';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.setTimeout(() => URL.revokeObjectURL(url), 0);
    setDownloaded(true);
    window.setTimeout(() => setDownloaded(false), 3000);
  };

  // Pill style mirrored from the landing nav-cta ("Request access"). Hover
  // is the same diagonal shimmer the main Button uses — see ui.tsx ::before
  // — applied via a className so we still get the inline-style ergonomics
  // for the rest of the look.
  const PILL_BASE = {
    position: 'relative' as const,
    overflow: 'hidden' as const,
    fontSize: 13,
    fontWeight: 600,
    padding: '9px 20px',
    borderRadius: 999,
    border: '1px solid rgba(210,166,103,0.32)',
    background: 'linear-gradient(180deg, rgba(210,166,103,0.14) 0%, rgba(210,166,103,0.05) 100%)',
    color: '#E8C892',
    letterSpacing: '0.2px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
    textDecoration: 'none',
    fontFamily: 'inherit',
    boxShadow: 'inset 0 1px 0 rgba(255,228,170,0.10)',
  };
  const PILL_SHIMMER_CSS = `
    .sp-pill::before {
      content: '';
      position: absolute;
      inset: -50% -120%;
      background: linear-gradient(120deg, transparent 35%, rgba(255,255,255,0.12) 50%, transparent 65%);
      transform: translateX(-40%);
      transition: transform 900ms ease;
      pointer-events: none;
    }
    .sp-pill:hover::before { transform: translateX(40%); }
    .sp-pill > * { position: relative; z-index: 1; }
  `;

  return (
    <div style={{
      display: 'grid',
      // Center vertically while the loading/opening animation is on stage.
      // Once the message is shown, pin to the top so the page reads top-down
      // — feels heavier and more "letter on a desk" than mid-air-centered.
      placeItems: animPhase === 'shown' ? 'start center' : 'center',
      minHeight: animPhase === 'shown' ? 'auto' : '100vh',
      paddingTop: animPhase === 'shown' ? '8vh' : 0,
      paddingBottom: animPhase === 'shown' ? '6vh' : 0,
      position: 'relative',
    }}>
      <style>{PILL_SHIMMER_CSS}</style>
      {animPhase === 'shown' ? <Embers /> : null}
      <div style={{ width: 'min(760px, 100%)', position: 'relative', zIndex: 1 }}>
        {animPhase !== 'loading' && animPhase !== 'opening' && !err ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            marginBottom: 32,
          }}>
            <img
              src="/logo.png"
              alt="SilentPost"
              style={{ width: 96, height: 96, objectFit: 'contain', opacity: 0.95 }}
            />
            <div style={{
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: 6,
              color: theme.colors.gold,
              textTransform: 'uppercase',
              marginTop: 14,
            }}>
              SilentPost
            </div>
            {/* Generous gap so the SilentPost mark feels like a header band
                separate from the message frame below it. */}
            <div style={{
              fontSize: 36,
              fontWeight: 800,
              letterSpacing: 0.3,
              marginTop: 56,
              color: theme.colors.text,
            }}>
              Final message
            </div>
            <Muted style={{ marginTop: 8, fontSize: 14 }}>
              If this reached you, it was intentional.
            </Muted>
          </div>
        ) : null}
        {animPhase === 'loading' ? (
          <RedeemAnimation phase="loading" />
        ) : animPhase === 'opening' ? (
          <RedeemAnimation phase="opening" />
        ) : err ? (
          <Card>
            <div style={{ color: theme.colors.danger, fontSize: 14, textAlign: 'center', padding: '12px 0' }}>
              This message doesn't exist.
            </div>
          </Card>
        ) : !hasMessage ? (
          <Card>
            <Field>
              <Label>Token</Label>
              <Input value={token} onChange={(e) => setToken(e.target.value)} placeholder="paste token" />
            </Field>
            <ButtonRow>
              <Button onClick={() => onRedeem()}>Open</Button>
            </ButtonRow>
          </Card>
        ) : (
          <>
            <Card>
              <CardTitle style={{ margin: 0 }}>Message</CardTitle>
              <Muted style={{ margin: '4px 0 0' }}>From {result.fromEmail ?? '—'}</Muted>
              <Hr />

              <MonoBox style={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: 15,
                lineHeight: 1.75,
                color: 'rgba(245, 245, 247, 0.82)',
              }}>{String(result.plaintext ?? '')}</MonoBox>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
                <button
                  type="button"
                  onClick={onDownloadMessage}
                  className="sp-pill"
                  style={PILL_BASE}
                >
                  {downloaded ? (
                    <>
                      <span style={{ fontWeight: 900 }}>✓</span> Downloaded
                    </>
                  ) : (
                    'Download message'
                  )}
                </button>
              </div>
            </Card>

            {Array.isArray(result.attachments) && result.attachments.length ? (
              <Card style={{ marginTop: 16 }}>
                <CardTitle>Attachments</CardTitle>
                <Hr />
                <div style={{ display: 'grid', gap: 12 }}>
                  {result.attachments.map((a: any, i: number) => {
                    const mime = String(a?.mime ?? '');
                    const url = String(a?.url ?? '');
                    const name = String(a?.name ?? `attachment-${i + 1}`);
                    const kind = fileIconKind(mime, name);
                    return (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                          <div style={{
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            width: 44, height: 44, flex: '0 0 44px',
                            borderRadius: 10,
                            border: '1px solid rgba(210,166,103,0.32)',
                            // Layered: base radial highlight from top-left for "lit
                            // from above" depth, plus a subtle vertical gradient.
                            background:
                              'radial-gradient(circle at 28% 20%, rgba(255,228,170,0.18), transparent 60%), ' +
                              'linear-gradient(180deg, rgba(210,166,103,0.16), rgba(210,166,103,0.04))',
                            // Inner-top highlight + faint inner-bottom shade
                            // for depth. No outer glow — keeps the icon clean.
                            boxShadow:
                              'inset 0 1px 0 rgba(255,228,170,0.14), ' +
                              'inset 0 -1px 0 rgba(0,0,0,0.18)',
                            color: '#E8C892',
                          }}>
                            <FileIcon kind={kind} size={24} />
                          </div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontWeight: 900, fontSize: 13, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</div>
                            <Muted style={{ fontSize: 12 }}>{simplifyMime(mime) ?? kind}</Muted>
                          </div>
                        </div>
                        <a
                          href={url}
                          target="_blank"
                          rel="noreferrer"
                          download={name}
                          className="sp-pill"
                          style={PILL_BASE}
                        >
                          Download
                        </a>
                      </div>
                    );
                  })}
                </div>
              </Card>
            ) : null}
          </>
        )}

        {animPhase === 'shown' ? (
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            marginTop: 24,
          }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={theme.colors.gold} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M12 3l8 3v5c0 5-3.5 8.5-8 10-4.5-1.5-8-5-8-10V6z" />
              <path d="M8.5 12.2l2.4 2.4 4.6-5" />
            </svg>
            <Muted style={{ fontSize: 12, textAlign: 'center' }}>This page expires in 24 hours.</Muted>
            <Muted style={{ fontSize: 12, textAlign: 'center' }}>Download anything you'd like to keep.</Muted>
          </div>
        ) : null}
      </div>
    </div>
  );
}
